/**
 * Mobile navigation toggle
 * @param event
 */

// Builds a Tab/Shift+Tab focus trap for `container`. Two modes:
//  - Wrap-in-place (default): boundaries wrap to the opposite end, staying
//    inside `container` — optionally landing on `wrapForwardTo` (a
//    tabindex="-1" heading override) instead of the first control, so
//    wrapping re-announces what the panel is rather than jumping straight
//    into its controls.
//  - External-trigger (`externalTrigger` supplied): for panels whose
//    open/close control lives OUTSIDE the trapped container (e.g. the primary
//    nav's toggle button, a sibling of <nav>, not a descendant). Both
//    boundaries hand focus off to `externalTrigger` and arm a one-shot
//    "released" flag instead of wrapping internally; a second listener on
//    `externalTrigger` itself completes the handshake — the next Tab either
//    re-enters the trap (first/last item, not armed) or, if armed, disarms
//    and calls `onRelease` while letting that Tab continue natively past the
//    trigger (no preventDefault) so focus lands wherever it naturally would.
// `activate()`/`deactivate()` attach/detach the listeners; only active while
// `isActiveBreakpoint()` is true, re-checked on every keypress as a safety
// net for "panel opened below the breakpoint, then window resized past it
// without closing."
const createFocusTrap = (
  container,
  {
    isActiveBreakpoint,
    wrapForwardTo = null,
    externalTrigger = null,
    onRelease = null
  } = {}
) => {
  let containerAttached = false
  let triggerAttached = false
  let released = false

  const getFocusable = () => getFocusableElements(container)

  const handleContainerKeydown = event => {
    if (!isActiveBreakpoint() || event.key !== 'Tab') return
    const focusable = getFocusable()
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (externalTrigger) {
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        released = true
        externalTrigger.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        released = true
        externalTrigger.focus()
      }
      return
    }

    const forwardTarget = wrapForwardTo || first
    if (event.shiftKey) {
      if (
        document.activeElement === first ||
        document.activeElement === wrapForwardTo
      ) {
        last.focus()
        event.preventDefault()
      }
    } else if (document.activeElement === last) {
      forwardTarget.focus()
      event.preventDefault()
    }
  }

  const handleTriggerKeydown = event => {
    if (!isActiveBreakpoint() || event.key !== 'Tab') return

    if (released) {
      released = false
      if (onRelease) onRelease()
      return
    }

    const focusable = getFocusable()
    if (!focusable.length) return
    ;(event.shiftKey ? focusable[focusable.length - 1] : focusable[0]).focus()
    event.preventDefault()
  }

  const activate = () => {
    if (!containerAttached) {
      container.addEventListener('keydown', handleContainerKeydown)
      containerAttached = true
    }
    if (externalTrigger && !triggerAttached) {
      externalTrigger.addEventListener('keydown', handleTriggerKeydown)
      triggerAttached = true
    }
  }

  const deactivate = () => {
    if (containerAttached) {
      container.removeEventListener('keydown', handleContainerKeydown)
      containerAttached = false
    }
    if (externalTrigger && triggerAttached) {
      externalTrigger.removeEventListener('keydown', handleTriggerKeydown)
      triggerAttached = false
    }
    released = false
  }

  return { activate, deactivate }
}

// Shared open/close primitive for full-panel overlays (primary nav, mobile
// filter panel) that use manual `inert`-based background locking. Native
// <dialog>-based modals (the video modals) intentionally don't go through
// this — they already get focus-trap/Escape/backdrop-click for free from the
// browser via showModal(), and folding them in here would be a step
// backward, not a consolidation.
const overlayPanels = []
const anyOverlayPanelOpen = () => overlayPanels.some(panel => panel.isOpen())

const createOverlayPanel = ({
  container,
  toggleTarget,
  openClass,
  isActiveBreakpoint,
  setInert,
  externalTrigger = null,
  wrapForwardTo = null,
  focusOnOpen = null,
  focusDelay = 0,
  restoreFocusTo = null,
  onBeforeOpen = null,
  onAfterClose = null
}) => {
  const isOpen = () => toggleTarget.classList.contains(openClass)

  function close({ restoreFocus = false } = {}) {
    if (!isOpen()) return
    toggleTarget.classList.remove(openClass)
    trap.deactivate()
    setInert(false)
    if (!anyOverlayPanelOpen()) document.body.classList.remove('modal-open')
    if (onAfterClose) onAfterClose()
    if (restoreFocus) {
      const el = restoreFocusTo && restoreFocusTo()
      if (el) el.focus()
    }
  }

  const trap = createFocusTrap(container, {
    isActiveBreakpoint,
    wrapForwardTo,
    externalTrigger,
    onRelease: () => close({ restoreFocus: false })
  })

  function open(event) {
    if (event) event.preventDefault()
    if (onBeforeOpen) onBeforeOpen()
    toggleTarget.classList.add(openClass)
    document.body.classList.add('modal-open')
    setInert(true)
    trap.activate()
    if (focusOnOpen) {
      const apply = () => {
        const el = focusOnOpen()
        if (el) el.focus()
      }
      if (focusDelay) setTimeout(apply, focusDelay)
      else apply()
    }
  }

  function toggle(event) {
    if (event) event.preventDefault()
    if (isOpen()) close({ restoreFocus: true })
    else open(event)
  }

  const panel = { open, close, toggle, isOpen }
  overlayPanels.push(panel)
  return panel
}

// Assigned once each panel is actually constructed further down (navPanel
// alongside setPrimaryNavBackgroundHidden, filterPanel inside
// initContentFilter) — referenced here and by callers above their
// construction point, safe because those references only run on a later
// user interaction, by which point the whole script has finished evaluating.
let navPanel = null
let filterPanel = null

document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return
  const openPanel = overlayPanels.find(panel => panel.isOpen())
  if (openPanel) openPanel.close({ restoreFocus: true })
})

// iPadOS 13+ reports its platform as "MacIntel", masquerading as desktop
// Safari, so a UA check alone can't tell an iPad from a real Mac —
// maxTouchPoints > 1 is what actually distinguishes them. Used to gate the
// no-blur-on-tap fallback timers below (search clear / filter clear
// buttons): those exist because iOS never moves focus to a <button> on tap,
// so blur never fires there to hide the button. On desktop, where blur does
// fire, the same timer would race ahead of it and hide the button while
// it's still focused.
const isIOSDevice =
  /iP(hone|od|ad)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

// iOS-only fallback for buttons that hide themselves on blur: since blur
// never fires there, hideIfStillActive runs on a timer instead, past the
// announcement each caller makes beforehand. No-op everywhere else, where
// the real blur listener already handles it — running this there too would
// hide the button while it's still focused.
//
// Deliberately later than announceStatus's own 500ms iOS write delay (not
// just after it): hiding this button moves focus, and VoiceOver's own
// focus-change speech from that can preempt/interrupt the live-region
// announcement if the two land close together, even when this timer is
// registered second and would otherwise win a same-instant tie. The 400ms
// gap gives VoiceOver time to actually start speaking the announcement
// before the button disappears out from under it.
const scheduleIOSHideFallback = hideIfStillActive => {
  if (!isIOSDevice) return
  setTimeout(hideIfStillActive, 900)
}

// Function to sanitize input
const sanitizeInput = input => {
  const element = document.createElement('div')
  element.innerText = input
  return element.innerHTML
}

const clearQueryStringParameter = paramName => {
  const url = new URL(window.location.href)
  url.searchParams.delete(paramName)
  history.replaceState({}, document.title, url.toString())
}

// Breakpoint-tiered offset for scrolling a target under the sticky header,
// plus a caller-supplied buffer for extra breathing room. Shared by the info
// accordion's scroll-to-top-on-open and the [data-scroll] link handler below.
const getScrollOffset = (buffer = 0) => {
  const width = window.innerWidth
  if (width >= 992) return 90 + buffer
  if (width >= 768) return 70 + buffer
  return 50 + buffer
}

// Header menu functions

// Shared by createFocusTrap (both the primary nav and the filter panel) and
// the nav submenus' own Tab handling below.
const focusableSelector =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), details:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'

const getFocusableElements = container => {
  if (!container) {
    return []
  }

  return Array.from(container.querySelectorAll(focusableSelector)).filter(
    el => {
      if (el.hasAttribute('disabled')) {
        return false
      }
      if (el.getAttribute('aria-hidden') === 'true') {
        return false
      }
      // offsetParent is null for position:fixed elements even when they're
      // fully visible and interactive (a long-standing cross-browser quirk,
      // not a display/visibility signal) — the filter panel's close button
      // is fixed-positioned so it stays put while the panel scrolls, and
      // offsetParent here would wrongly drop it from the trap's boundary
      // detection. getClientRects().length correctly reflects "does this
      // generate a rendered box" regardless of position.
      return el.getClientRects().length > 0 || el === document.activeElement
    }
  )
}

const updatePrimaryMenuAria = () => {
  const toggle = document.querySelector('.js-menu-toggle')
  const nav = document.querySelector('.header__navigation')
  if (!toggle || !nav) {
    return
  }

  const expanded = nav.classList.contains('menu-open')
  toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false')
}

// navPanel is constructed further down (alongside setPrimaryNavBackgroundHidden,
// which it needs) but only referenced here inside a later click handler — see
// the note by its declaration for why that's safe.
const toggleMenu = event => {
  navPanel.toggle(event)
}

document
  .querySelectorAll('.js-menu-toggle')
  .forEach(el => el.addEventListener('click', toggleMenu))

const primaryMenuToggle = document.querySelector('.js-menu-toggle')
if (primaryMenuToggle && !primaryMenuToggle.hasAttribute('aria-expanded')) {
  primaryMenuToggle.setAttribute('aria-expanded', 'false')
}
updatePrimaryMenuAria()

const getSecondaryMenuTrigger = item => {
  if (!item) return null
  const { children } = item
  for (let i = 0; i < children.length; i += 1) {
    const child = children[i]
    if (
      child &&
      typeof child.matches === 'function' &&
      child.matches('a, button')
    ) {
      return child
    }
  }
  return null
}

const hasActiveSecondaryItem = () =>
  document.querySelector('.secondary__nav-menu-item.active') !== null

const updateSecondaryMenuAria = () => {
  const items = document.querySelectorAll('.secondary__nav-menu-item')
  items.forEach(item => {
    const trigger = getSecondaryMenuTrigger(item)
    if (!trigger) return
    const expanded = item.classList.contains('active')
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false')
  })

  const secondaryToggle = document.querySelector('.secondary_nav')
  if (secondaryToggle) {
    const expanded =
      document.body.classList.contains('secondary-menu-opened') ||
      !!document.querySelector('.secondary__navigation.active')
    secondaryToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false')
  }
}

const closeSecondaryMenuItem = menuItem => {
  if (!menuItem) {
    return
  }

  menuItem.classList.remove('active')
  if (!hasActiveSecondaryItem()) {
    document.body.classList.remove('secondary-menu-opened')
  }
  updateSecondaryMenuAria()
}

const closeDesktopSecondaryMenu = () => {
  document
    .querySelectorAll('.secondary__nav-menu-item')
    .forEach(el => el.classList.remove('active'))
  document.body.classList.remove('secondary-menu-opened')
  updateSecondaryMenuAria()
}

// navPanel.close() already no-ops if the nav isn't open, so callers below
// (outside-click, resize, focusout) can invoke this unconditionally.
function closePrimaryMenu(opts) {
  navPanel.close(opts)
  closeDesktopSecondaryMenu()
}

const headerNavigation = document.querySelector('.header__navigation')
const mql = window.matchMedia('(max-width: 1199px)')

mql.onchange = e => {
  // Deliberately navPanel.close(), not closePrimaryMenu(): a desktop-breakpoint
  // resize doesn't touch the desktop secondary menu's own open state.
  if (!e.matches) {
    navPanel.close({ restoreFocus: false })
  }
  updateSecondaryMenuAria()
}

// Keep outside-pointer dismissal off `click`: a document-wide click listener
// can make Chrome expose otherwise static text as clickable to VoiceOver.
document.addEventListener('pointerup', event => {
  if (
    event.isPrimary &&
    event.button === 0 &&
    primaryMenuToggle &&
    headerNavigation &&
    !primaryMenuToggle.contains(event.target) &&
    !headerNavigation.contains(event.target)
  ) {
    closePrimaryMenu({ restoreFocus: false })
  }
})

// Independent of the Tab-key trap: catches any way focus could leave the nav
// (mouse click elsewhere, programmatic .focus(), AT commands) that Tab
// interception structurally can't see.
const handleMobileNavFocusOut = event => {
  if (!mql.matches) {
    return
  }
  if (!headerNavigation || !headerNavigation.classList.contains('menu-open')) {
    return
  }

  const nextTarget = event.relatedTarget
  if (!nextTarget) {
    return
  }
  const isFocusInsideNav = headerNavigation.contains(nextTarget)
  const isFocusOnToggle =
    primaryMenuToggle && primaryMenuToggle.contains(nextTarget)

  if (!isFocusInsideNav && !isFocusOnToggle) {
    closePrimaryMenu({ restoreFocus: false })
  }
}

if (headerNavigation) {
  headerNavigation.addEventListener('focusout', handleMobileNavFocusOut)
}

const handleSecondaryMenuFocus = event => {
  if (mql.matches) {
    return
  }

  if (!document.body.classList.contains('secondary-menu-opened')) {
    return
  }

  const parentItem = event.currentTarget.closest('.secondary__nav-menu-item')
  if (parentItem && parentItem.classList.contains('active')) {
    return
  }

  closeDesktopSecondaryMenu()
}

document
  .querySelectorAll('.secondary__nav-menu-item > button.fw-bold')
  .forEach(link => {
    link.addEventListener('focus', handleSecondaryMenuFocus)
    link.setAttribute('aria-expanded', 'false')
  })

const handleSecondaryMenuFocusOut = event => {
  if (mql.matches) {
    return
  }

  const item = event.currentTarget
  if (!item.contains(event.relatedTarget)) {
    if (item.classList.contains('active')) {
      closeDesktopSecondaryMenu()
    }
  }
}

document.querySelectorAll('.secondary__nav-menu-item').forEach(item => {
  item.addEventListener('focusout', handleSecondaryMenuFocusOut)
})

updateSecondaryMenuAria()

const openSecondaryNav = event => {
  event.preventDefault()
  const secondaryNav = document.querySelector('.secondary__navigation')
  const menuItems = document.querySelectorAll('.secondary__nav-menu-item')

  if (secondaryNav && secondaryNav.classList.contains('active')) {
    secondaryNav.classList.remove('active')
    menuItems.forEach(item => item.classList.remove('menu--closed'))
  } else {
    if (secondaryNav) secondaryNav.classList.add('active')
    menuItems.forEach(item => item.classList.add('menu--closed'))
  }

  updateSecondaryMenuAria()
}

document
  .querySelectorAll('.secondary_nav')
  .forEach(el => el.addEventListener('click', openSecondaryNav))

const secondaryNavToggle = document.querySelector('.secondary_nav')
if (secondaryNavToggle) {
  secondaryNavToggle.setAttribute(
    'aria-expanded',
    secondaryNavToggle.getAttribute('aria-expanded') || 'false'
  )
}

const hideAlert = event => {
  event.preventDefault()
  document.querySelectorAll('.alert').forEach(el => {
    el.style.display = 'none'
  })
  document
    .querySelectorAll('.header')
    .forEach(el => el.classList.remove('has-alert'))
}

document
  .querySelectorAll('.alert button.alert--close')
  .forEach(el => el.addEventListener('click', hideAlert))

// Open secondary menu on desktop
const toggleMobileSubNav = event => {
  event.preventDefault()
  const parentNode = event.currentTarget.closest('.secondary__nav-menu-item')
  if (!parentNode) {
    return
  }

  const wasActive = parentNode.classList.contains('active')
  document.querySelectorAll('.secondary__nav-menu-item').forEach(item => {
    if (item !== parentNode) {
      item.classList.remove('active')
    }
  })

  parentNode.classList.toggle('active', !wasActive)

  if (hasActiveSecondaryItem()) {
    document.body.classList.add('secondary-menu-opened')
  } else {
    document.body.classList.remove('secondary-menu-opened')
  }

  updateSecondaryMenuAria()
}

document
  .querySelectorAll('.secondary__nav-menu-item > button.fw-bold')
  .forEach(el => el.addEventListener('click', toggleMobileSubNav))

const handleSubmenuKeydown = event => {
  if (event.key !== 'Tab' || !mql.matches) {
    return
  }

  const nav = event.currentTarget
  const menuItem = nav.closest('.secondary__nav-menu-item')
  if (!menuItem || !menuItem.classList.contains('active')) {
    return
  }

  const focusableElements = getFocusableElements(nav)
  if (!focusableElements.length) {
    closeSecondaryMenuItem(menuItem)
    return
  }

  const firstFocusable = focusableElements[0]
  const lastFocusable = focusableElements[focusableElements.length - 1]

  if (event.shiftKey) {
    if (firstFocusable && firstFocusable.contains(event.target)) {
      closeSecondaryMenuItem(menuItem)
    }
    return
  }

  if (lastFocusable && lastFocusable.contains(event.target)) {
    closeSecondaryMenuItem(menuItem)
  }
}

document
  .querySelectorAll('.secondary__nav-menu-item .secondary__navigation')
  .forEach(submenu => {
    submenu.addEventListener('keydown', handleSubmenuKeydown)
  })

// Vertical tabbed component (desktop tabs + mobile accordion)
let verticalTabsCleanup = null

const initVerticalTabbedContainers = () => {
  if (typeof verticalTabsCleanup === 'function') {
    verticalTabsCleanup()
    verticalTabsCleanup = null
  }

  const containers = document.querySelectorAll(
    '.vertical-tabbed-container,.tabs-and-accordion-container'
  )
  if (!containers.length) {
    return
  }

  const mobileQuery = window.matchMedia('(max-width: 768px)')
  const cleanupHandlers = []

  containers.forEach(container => {
    const tabs = Array.from(container.querySelectorAll('.tab'))
    const tabPanels = Array.from(container.querySelectorAll('.tab-panel'))
    const accordionToggles = Array.from(
      container.querySelectorAll('.tab-panel-toggle')
    )

    if (!tabs.length || !tabPanels.length) {
      return
    }

    // A vertical tablist steps with Up/Down, a horizontal one with Left/Right.
    const tabList = container.querySelector('[role="tablist"]')
    const isVerticalTabList =
      tabList && tabList.getAttribute('aria-orientation') === 'vertical'
    const nextTabKey = isVerticalTabList ? 'ArrowDown' : 'ArrowRight'
    const previousTabKey = isVerticalTabList ? 'ArrowUp' : 'ArrowLeft'

    const defaultTabIndex = tabs[0]
      ? tabs[0].getAttribute('data-tab-index')
      : null
    let currentActiveIndex = null
    let lastDesktopIndex = defaultTabIndex

    const isMobileView = () => mobileQuery.matches

    // This component is a tab set on desktop and a disclosure set on mobile, where CSS
    // takes the tablist to display:none. A tabpanel whose owning tab has left the a11y
    // tree is orphaned, so the panel's role and label source swap with the breakpoint:
    // desktop labels the tabpanel from its tab, mobile labels a region from its toggle.
    //
    // Both sides are derived from ids the template owns, never from the panel's current
    // role/aria-labelledby. init() runs twice per load (DOMContentLoaded and pageshow),
    // so reading the live DOM for the "desktop" baseline lets the second pass capture
    // whatever the first pass already swapped in.
    const panelSemantics = tabPanels.map(panel => {
      const panelIndex = panel.getAttribute('data-tab-index')
      const tab = tabs.find(
        candidate => candidate.getAttribute('data-tab-index') === panelIndex
      )
      const toggle = panel.querySelector('.tab-panel-toggle')
      return {
        main: panel.querySelector('.tab-panel-main'),
        tabId: tab && tab.id,
        toggleId: toggle && toggle.id
      }
    })

    // APG allows role=region on accordion panels, but warns against landmark
    // proliferation past roughly six of them.
    const mobilePanelRole = tabPanels.length > 6 ? 'group' : 'region'

    const updatePanelSemantics = mobileView => {
      panelSemantics.forEach(({ main, tabId, toggleId }) => {
        if (!main) {
          return
        }

        if (mobileView) {
          if (toggleId) {
            main.setAttribute('role', mobilePanelRole)
            main.setAttribute('aria-labelledby', toggleId)
          }
          return
        }

        main.setAttribute('role', 'tabpanel')
        if (tabId) {
          main.setAttribute('aria-labelledby', tabId)
        }
      })
    }

    const updateResponsiveAccessibility = () => {
      const mobileView = isMobileView()

      updatePanelSemantics(mobileView)

      tabs.forEach(tab => {
        if (mobileView) {
          tab.setAttribute('aria-hidden', 'true')
          tab.setAttribute('tabindex', '-1')
        } else {
          const isActiveTab = tab.classList.contains('active')
          tab.setAttribute('aria-hidden', 'false')
          tab.setAttribute('tabindex', isActiveTab ? '0' : '-1')
        }
      })

      accordionToggles.forEach(toggle => {
        if (mobileView) {
          toggle.setAttribute('aria-hidden', 'false')
          toggle.setAttribute('tabindex', '0')
        } else {
          toggle.setAttribute('aria-hidden', 'true')
          toggle.setAttribute('tabindex', '-1')
        }
      })
    }

    const setActiveState = tabIndex => {
      currentActiveIndex = tabIndex

      tabs.forEach(tab => {
        const matches = tab.getAttribute('data-tab-index') === tabIndex
        const isActiveTab = tabIndex !== null && matches
        tab.classList.toggle('active', isActiveTab)
        tab.setAttribute('aria-selected', isActiveTab ? 'true' : 'false')
      })

      tabPanels.forEach(panel => {
        const isActivePanel =
          tabIndex !== null && panel.getAttribute('data-tab-index') === tabIndex
        panel.classList.toggle('active', isActivePanel)
        // Remove inactive panels from focus/AT tree while they remain in the DOM
        // (panels are stacked via CSS grid so the container sizes to the tallest).
        if (isMobileView()) {
          panel.removeAttribute('inert')
        } else {
          panel.toggleAttribute('inert', !isActivePanel)
        }

        const panelMain = panel.querySelector('.tab-panel-main')
        if (panelMain) {
          panelMain.setAttribute(
            'aria-hidden',
            isActivePanel ? 'false' : 'true'
          )
          panelMain.setAttribute('tabindex', isActivePanel ? '0' : '-1')
        }

        const toggle = panel.querySelector('.tab-panel-toggle')
        if (toggle) {
          toggle.setAttribute('aria-expanded', isActivePanel ? 'true' : 'false')
        }
      })

      updateResponsiveAccessibility()
    }

    const activateTab = tabIndex => {
      if (tabIndex === null || typeof tabIndex === 'undefined') {
        setActiveState(null)
        return
      }

      lastDesktopIndex = tabIndex
      setActiveState(tabIndex)
    }

    const deactivateAllTabs = () => {
      setActiveState(null)
    }

    const handleTabClick = event => {
      const tabIndex = event.currentTarget.getAttribute('data-tab-index')
      if (isMobileView() && currentActiveIndex === tabIndex) {
        deactivateAllTabs()
        return
      }

      activateTab(tabIndex)
    }

    const focusNextOutsideContainer = startElement => {
      if (!startElement) {
        return false
      }

      const focusableSelector =
        'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
      const focusableElements = Array.from(
        document.querySelectorAll(focusableSelector)
      ).filter(el => {
        if (el.hasAttribute('disabled')) {
          return false
        }
        if (el.getAttribute('aria-hidden') === 'true') {
          return false
        }
        if (el === startElement) {
          return true
        }
        return el.offsetParent !== null
      })

      const currentIndex = focusableElements.indexOf(startElement)
      if (currentIndex === -1) {
        return false
      }

      for (let i = currentIndex + 1; i < focusableElements.length; i += 1) {
        const candidate = focusableElements[i]
        if (!container.contains(candidate)) {
          candidate.focus()
          return true
        }
      }

      return false
    }

    const focusAssociatedPanelContent = tabElement => {
      if (!tabElement) {
        return false
      }

      const tabIndexValue = tabElement.getAttribute('data-tab-index')
      if (tabIndexValue === null) {
        return false
      }

      const panelMain = container.querySelector(
        `.tab-panel[data-tab-index="${tabIndexValue}"] .tab-panel-main`
      )
      if (!panelMain) {
        return false
      }

      panelMain.focus()
      return true
    }

    const handleTabKeydown = event => {
      const { key } = event
      const currentIndex = tabs.indexOf(event.currentTarget)
      let targetIndex = null

      if (key === nextTabKey) {
        targetIndex = (currentIndex + 1) % tabs.length
      } else if (key === previousTabKey) {
        targetIndex = (currentIndex - 1 + tabs.length) % tabs.length
      } else if (key === 'Home') {
        targetIndex = 0
      } else if (key === 'End') {
        targetIndex = tabs.length - 1
      }

      if (targetIndex !== null) {
        event.preventDefault()
        const targetTab = tabs[targetIndex]
        targetTab.focus()
        activateTab(targetTab.getAttribute('data-tab-index'))
        return
      }

      if (key === 'Tab' && !event.shiftKey) {
        if (isMobileView()) {
          return
        }
        const isLastTab = currentIndex === tabs.length - 1
        const isActive = event.currentTarget.classList.contains('active')

        if (isLastTab && isActive) {
          const didFocusPanel = focusAssociatedPanelContent(event.currentTarget)
          if (didFocusPanel) {
            event.preventDefault()
          }
          return
        }

        if (isLastTab) {
          const movedFocus = focusNextOutsideContainer(event.currentTarget)
          if (movedFocus) {
            event.preventDefault()
          }
          return
        }

        if (isActive) {
          const didFocusPanel = focusAssociatedPanelContent(event.currentTarget)
          if (didFocusPanel) {
            event.preventDefault()
          }
        }
      }
    }

    tabs.forEach(tab => {
      tab.addEventListener('click', handleTabClick)
      tab.addEventListener('keydown', handleTabKeydown)
    })

    cleanupHandlers.push(() => {
      tabs.forEach(tab => {
        tab.removeEventListener('click', handleTabClick)
        tab.removeEventListener('keydown', handleTabKeydown)
      })
    })

    const scrollPanelIntoView = tabIndex => {
      const activePanel = container.querySelector(
        `.tab-panel[data-tab-index="${tabIndex}"]`
      )
      if (!activePanel) {
        return
      }

      const scrollTarget =
        activePanel.querySelector('.tab-panel-toggle') || activePanel
      const header = document.querySelector('.header')
      const offset = header ? header.offsetHeight : 0
      const buffer = 16
      const targetTop = Math.max(
        scrollTarget.getBoundingClientRect().top +
          window.pageYOffset -
          offset -
          buffer,
        0
      )

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      })
    }

    // Deferred a frame + 80ms so the panel's open/height transition has
    // started before measuring its position to scroll to.
    const deferScrollPanelIntoView = tabIndex => {
      window.requestAnimationFrame(() => {
        setTimeout(() => {
          scrollPanelIntoView(tabIndex)
        }, 80)
      })
    }

    const handleAccordionToggle = event => {
      if (!mobileQuery.matches) {
        return
      }

      const tabIndex = event.currentTarget.getAttribute('data-tab-index')
      const isAlreadyActive = currentActiveIndex === tabIndex

      if (isAlreadyActive) {
        deactivateAllTabs()
        return
      }

      activateTab(tabIndex)
      deferScrollPanelIntoView(tabIndex)
    }

    const handleToggleFocus = event => {
      if (!mobileQuery.matches) {
        return
      }

      const tabIndex = event.currentTarget.getAttribute('data-tab-index')
      if (tabIndex === null) {
        return
      }

      deferScrollPanelIntoView(tabIndex)
    }

    accordionToggles.forEach(toggle => {
      toggle.addEventListener('click', handleAccordionToggle)
      toggle.addEventListener('focus', handleToggleFocus)
    })

    cleanupHandlers.push(() => {
      accordionToggles.forEach(toggle => {
        toggle.removeEventListener('click', handleAccordionToggle)
        toggle.removeEventListener('focus', handleToggleFocus)
      })
    })

    // activateTab/deactivateAllTabs both end in setActiveState, which already
    // calls updateResponsiveAccessibility — only the no-default-tab case
    // needs to call it directly here.
    const initializeState = () => {
      if (isMobileView()) {
        deactivateAllTabs()
        return
      }

      if (defaultTabIndex !== null) {
        activateTab(defaultTabIndex)
      } else {
        updateResponsiveAccessibility()
      }
    }

    initializeState()

    const handleBreakpointChange = event => {
      if (event.matches) {
        deactivateAllTabs()
        return
      }

      const targetIndex =
        currentActiveIndex || lastDesktopIndex || defaultTabIndex
      if (targetIndex !== null) {
        activateTab(targetIndex)
      }
    }

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleBreakpointChange)
      cleanupHandlers.push(() => {
        mobileQuery.removeEventListener('change', handleBreakpointChange)
      })
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(handleBreakpointChange)
      cleanupHandlers.push(() => {
        mobileQuery.removeListener(handleBreakpointChange)
      })
    }
  })

  if (!cleanupHandlers.length) {
    return
  }

  let hasCleanedUp = false

  const runCleanup = () => {
    if (hasCleanedUp) {
      return
    }
    hasCleanedUp = true

    while (cleanupHandlers.length) {
      const cleanup = cleanupHandlers.pop()
      if (typeof cleanup === 'function') {
        cleanup()
      }
    }
  }

  const handleBeforeUnload = () => {
    runCleanup()
  }

  const handlePageHide = event => {
    if (event && event.persisted) {
      return
    }
    runCleanup()
  }

  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('pagehide', handlePageHide)

  cleanupHandlers.push(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('pagehide', handlePageHide)
  })

  verticalTabsCleanup = runCleanup
}

document.addEventListener('DOMContentLoaded', initVerticalTabbedContainers)

window.addEventListener('pageshow', () => {
  initVerticalTabbedContainers()
})

// Info accordion: button + region disclosure (aria-expanded / hidden) with a
// GSAP-driven height animation. No scroll on open — moving the page while the
// panel expands left VoiceOver's focus box behind at the old position. GSAP is
// enqueued by the component template and may load after this script — so we
// check for it at click time.
;(() => {
  const triggers = document.querySelectorAll(
    'section.info-accordion .accordion-trigger'
  )
  if (!triggers.length) return

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches

  const OPEN_DURATION = 0.4
  const CLOSE_DURATION = 0.3

  triggers.forEach(trigger => {
    const item = trigger.closest('.button-row')
    const panel = document.getElementById(trigger.getAttribute('aria-controls'))
    if (!item || !panel) return

    trigger.addEventListener('click', () => {
      const opening = trigger.getAttribute('aria-expanded') !== 'true'
      const useGsap = !!window.gsap && !prefersReducedMotion

      // State flips immediately so screen readers hear the new value.
      trigger.setAttribute('aria-expanded', String(opening))
      item.classList.toggle('active', opening)

      if (!useGsap) {
        panel.hidden = !opening
        return
      }

      gsap.killTweensOf(panel)

      if (opening) {
        panel.hidden = false
        gsap.fromTo(
          panel,
          { height: 0 },
          {
            height: panel.scrollHeight,
            duration: OPEN_DURATION,
            ease: 'power2.inOut',
            onComplete: () => gsap.set(panel, { clearProps: 'height' })
          }
        )
        return
      }

      gsap.to(panel, {
        height: 0,
        duration: CLOSE_DURATION,
        ease: 'power2.inOut',
        onComplete: () => {
          panel.hidden = true
          gsap.set(panel, { clearProps: 'height' })
        }
      })
    })
  })
})()

// animated scroll. just add data-scroll="id-of-target" to the link
const scrollLinks = document.querySelectorAll('[data-scroll]')
const DATA_SCROLL_BUFFER = 50

scrollLinks.forEach(link => {
  const target = link.dataset.scroll
  link.addEventListener('click', event => {
    event.preventDefault()

    const targetEl = document.querySelector(`#${target}`)
    if (!targetEl) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    const focusTarget = () => {
      if (!targetEl.hasAttribute('tabindex')) {
        targetEl.setAttribute('tabindex', '-1')
      }
      targetEl.focus({ preventScroll: true })
    }

    if (window.gsap && window.ScrollToPlugin && !prefersReducedMotion) {
      gsap.registerPlugin(ScrollToPlugin)
      gsap.to(window, {
        duration: 0.8,
        scrollTo: { y: targetEl, offsetY: getScrollOffset(DATA_SCROLL_BUFFER) },
        autoKill: true,
        ease: 'power2.inOut',
        onComplete: focusTarget
      })
      return
    }

    const targetTop =
      targetEl.getBoundingClientRect().top +
      window.scrollY -
      getScrollOffset(DATA_SCROLL_BUFFER)
    window.scrollTo({
      top: targetTop,
      left: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    })
    focusTarget()
  })
})

// Social sharing aid
const socialIcons = document.querySelectorAll('.post__social-icon')

socialIcons.forEach(el => {
  el.addEventListener('click', event => {
    event.preventDefault()
    const name = event.currentTarget.dataset.socialSharing
    const url = event.currentTarget.href
    const width = 500
    const height = 300
    const top = (window.outerHeight - height) / 2
    const left = (window.outerWidth - width) / 2
    const features = `width=${width},height=${height},left=${left},top=${top},popup=yes`

    if (name !== 'Clipboard') {
      window.open(url, name, features)
    } else {
      copyToClickBoard(`${window.location.origin}${window.location.pathname}`)
    }
  })
})

const copyToClickBoard = async content => {
  const notice = document.querySelector('.clipboard-notice')

  try {
    // Use modern Clipboard API if available
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(content)
    } else {
      // Fallback for older browsers or non-secure contexts
      const body = document.querySelector('body')
      const area = document.createElement('textarea')

      // Position the textarea off-screen
      area.style.position = 'absolute'
      area.style.left = '-9999px'
      area.style.width = '0'
      area.style.height = '0'

      body.appendChild(area)

      area.value = content
      area.select()
      document.execCommand('copy')

      body.removeChild(area)
    }

    notice.style.display = 'block'
    setTimeout(() => {
      notice.style.display = 'none'
    }, 20000)
  } catch (error) {
    console.error('Failed to copy text: ', error)
  }
}

const links = document.querySelectorAll('[data-url]')
links.forEach(link => {
  link.addEventListener('click', event => {
    event.preventDefault() // Prevent the default link behavior
    copyToClickBoard(link.dataset.url)
  })
})

const toggleRegionViews = () => {
  document
    .querySelectorAll('.text-modal')
    .forEach(el => el.classList.toggle('active'))
  document
    .querySelectorAll('.img-area img')
    .forEach(el => el.classList.toggle('inactive'))
}

const textVersionLink = document.querySelector('#text-version-link')
const mapVersionLink = document.querySelector('#map-version-link')

const showRegionList = event => {
  event.preventDefault()
  toggleRegionViews()
  if (textVersionLink) textVersionLink.classList.add('inactive')
  if (mapVersionLink) mapVersionLink.classList.add('active')
}

if (textVersionLink) {
  textVersionLink.addEventListener('click', showRegionList)
}

const showMapList = event => {
  event.preventDefault()
  toggleRegionViews()
  if (mapVersionLink) mapVersionLink.classList.remove('active')
  if (textVersionLink) textVersionLink.classList.remove('inactive')
}

if (mapVersionLink) {
  mapVersionLink.addEventListener('click', showMapList)
}
// Filter: Research Center, Article Hub
const content = document.querySelector('#content')
const searchBoxWrapper = document.querySelector('.search')
const filters = document.querySelectorAll('.form-check-input')
const clearFiltersBtn = document.querySelector('.form-clear-button')
const mobileFilterOpenBtn = document.querySelector('#filter-dropdown')
const mobileFilterCloseBtn = document.querySelector('#filter-dropdown-close')
const viewResults = document.querySelector('#view-results')
const filterLabels = document.querySelectorAll('#results-found span.filter')
const filterResultsLabel = document.querySelector(
  '#results-found .filters-selected strong'
)
let filteredList = 0

// Toggle `inert` on every direct child of `root` that doesn't contain `keepEl`,
// so `keepEl` (and the plain wrapper elements leading to it) stays focusable/
// readable while everything else under `root` drops out of the tab order and
// the accessibility tree. `inert` cascades to descendants, so this has to walk
// siblings of `keepEl`'s ancestor chain rather than inerting `root` itself.
const setInertExcept = (root, keepEl, isInert) => {
  if (!root || !keepEl) return

  Array.from(root.children).forEach(child => {
    if (child === keepEl || child.contains(keepEl)) {
      if (child !== keepEl) setInertExcept(child, keepEl, isInert)
    } else {
      child.toggleAttribute('inert', isInert)
    }
  })
}

const siteHeader = document.querySelector('.header[role="banner"]')
const siteFooter = document.querySelector('.global-footer')
const navBurgerBtn = document.querySelector('.nav-burger.js-menu-toggle')

const setMobileFilterMenuBackgroundHidden = isHidden => {
  // Inert the header except the main nav toggle itself — it lives inside
  // siteHeader, so a blanket toggleAttribute('inert') here would trap the
  // user with no way to reach the primary menu while the filter panel is open.
  if (siteHeader && navBurgerBtn) {
    setInertExcept(siteHeader, navBurgerBtn, isHidden)
  } else if (siteHeader) {
    siteHeader.toggleAttribute('inert', isHidden)
  }
  if (siteFooter) siteFooter.toggleAttribute('inert', isHidden)

  const openModal = mobileFilterOpenBtn
    ? document.getElementById(mobileFilterOpenBtn.getAttribute('aria-controls'))
    : null
  setInertExcept(content, openModal, isHidden)
}

// The primary mobile nav toggle/panel live inside <header>, so unlike the
// filter menu there's no "keep this one descendant interactive" case - the
// whole main content region and footer can just be inerted outright.
const setPrimaryNavBackgroundHidden = isHidden => {
  if (content) content.toggleAttribute('inert', isHidden)
  if (siteFooter) siteFooter.toggleAttribute('inert', isHidden)
}

navPanel = createOverlayPanel({
  container: headerNavigation,
  toggleTarget: headerNavigation,
  openClass: 'menu-open',
  isActiveBreakpoint: () => mql.matches,
  setInert: setPrimaryNavBackgroundHidden,
  externalTrigger: primaryMenuToggle,
  restoreFocusTo: () => primaryMenuToggle,
  onBeforeOpen: () => {
    if (primaryMenuToggle) primaryMenuToggle.classList.add('open')
    // The mobile filter panel and the primary nav are both full-panel
    // overlays; having both open at once stacks one on top of the other
    // with no way to reach whichever is underneath.
    if (filterPanel && filterPanel.isOpen()) {
      filterPanel.close({ restoreFocus: false })
    }
  },
  onAfterClose: () => {
    if (primaryMenuToggle) primaryMenuToggle.classList.remove('open')
    updatePrimaryMenuAria()
  }
})

if (mobileFilterOpenBtn) {
  mobileFilterOpenBtn.addEventListener('click', event =>
    filterPanel.open(event)
  )
  mobileFilterCloseBtn.addEventListener('click', () =>
    filterPanel.close({ restoreFocus: true })
  )
  viewResults.addEventListener('click', () =>
    filterPanel.close({ restoreFocus: true })
  )

  // The filter panel inerts the rest of #content while open (see
  // setMobileFilterMenuBackgroundHidden), which includes #results-list — so
  // without this, tapping "Skip to results" from inside the open mobile menu
  // targets an inert element and silently does nothing. No restoreFocus
  // here: closing the menu just needs to happen before the link's native
  // hash navigation carries focus to #results-list, not instead of it.
  const skipToResultsLink = document.querySelector('#skip-to-results a')
  if (skipToResultsLink) {
    skipToResultsLink.addEventListener('click', () => {
      filterPanel.close({ restoreFocus: false })
    })
  }
}

// Announce a status message through the persistent sr-only live region. Deferred
// so VoiceOver treats it as a discrete event: VO drops live-region updates that
// fire synchronously with a click/activation, and stays silent when a toggle
// button switches off — so callers announce the outcome explicitly here instead.
// The visual counts live in display:none containers and can't reliably announce.
let statusAnnounceTimer
const announceStatus = message => {
  const region = document.querySelector('[data-results-announce]')
  if (!region) return
  clearTimeout(statusAnnounceTimer)
  // Blank the region immediately so the delayed write below is always a real
  // text mutation, even when this message is identical to the last one. iOS
  // VoiceOver silently skips a live-region update whose text doesn't change,
  // which is why announcements were landing "randomly" — only when the new
  // message happened to differ from whatever was last announced.
  region.textContent = ''
  // iOS VoiceOver needs more headroom than desktop VO/NVDA before it reliably
  // picks up a live-region update — QA found plain filter-select/clear
  // announcements going unheard there. 500ms mirrors the delay this file
  // already settled on for the deep-link announcement after hitting the same
  // "too short, gets swallowed" symptom (see scheduleQueryFilterAnnouncement).
  // A timing adjustment pending AT retest, not a confirmed fix.
  statusAnnounceTimer = setTimeout(
    () => {
      region.textContent = message
    },
    isIOSDevice ? 500 : 150
  )
}

// Current results count as text, e.g. "20 results" (or the total when unfiltered).
// textContent, not innerText: innerText is render-dependent (spec'd to reflect
// what's actually laid out on screen), and this element's nearest match sits
// inside a container that goes display:none exactly when filters clear to
// zero — Clear tags and toggling off the last tag are the two cases where
// the count came back missing from the announcement. textContent reads the
// raw text regardless of visibility, which is what an accessibility
// announcement should be using here anyway.
const currentResultsText = () => {
  const el = document.querySelector('.num-of-results')
  return el ? el.textContent.trim().replace(/\s+/g, ' ') : ''
}

const updateResultsQty = num => {
  const qty = document.querySelectorAll('.num-of-results')
  const results = document.querySelector('.content-filtering')
  if (typeof num === 'number') {
    results.classList.add('active')
    const label = `${num} ${num === 1 ? 'result' : 'results'}`
    qty.forEach(item => {
      item.innerText = label
    })
    const filterNum = document.querySelectorAll(
      '#results-found span.filter:not(.unselected)'
    ).length
    filterResultsLabel.innerText = filterResultsLabel.innerText.replace(
      /filters|filter/,
      filterNum === 1 ? 'filter' : 'filters'
    )
  } else {
    results.classList.remove('active')
    qty.forEach(item => {
      item.innerText = `${item.dataset.resultsNum} results`
    })
  }
}

const addOrRemove = (arr, item) =>
  arr.includes(item) ? arr.filter(el => el !== item) : [...arr, item]
const addUnique = (arr, item) => (!arr.includes(item) ? [...arr, item] : arr)
const resetList = tiles => {
  tiles.forEach(tile => {
    tile.classList.remove('hidden')
  })
}

const toggleFilterWrapper = enable => {
  const filterBox = document.querySelector('.filters')
  const filters = document.querySelectorAll('.filters .form-check-input')

  if (enable) {
    filterBox.classList.remove('disabled')
    filters.forEach(filter => {
      filter.disabled = false
    })
  } else {
    filterBox.classList.add('disabled')
    filters.forEach(filter => {
      filter.disabled = true
    })
  }
}

const disableSearchBox = () => {
  searchBoxWrapper.classList.toggle('disabled', true)
  searchBox.setAttribute('disabled', true)
  searchBtn.setAttribute('disabled', true)
}
const enableSearchBox = () => {
  searchBoxWrapper.classList.toggle('disabled', false)
  searchBox.removeAttribute('disabled')
  updateSearchBtnState()
}

// Content Search box
const search = document.querySelector('.form-search-box')
const searchBox = document.querySelector('#search-box')
const searchBtn = document.querySelector('#search-btn')
const searchTerm = document.querySelector('#search-term')
const clearSearchBtn = document.querySelector('#search-clear-btn')
const tiles = document.querySelectorAll('[data-summarized]')
const initResultsQty = document.querySelector('#results-found')
// The filterable page's <main> (Research Center or Article Hub). Its `.searching`
// class reveals the mobile floating results count, mirroring the filter flow's `.filtered`.
const contentFilterMain = document.querySelector(
  'main.research-center, main.article-hub'
)
const isArticleHub = document.body.classList.contains(
  'post-type-archive-article-hub'
)

// Search button (and Enter key) stays disabled until enough characters are typed
const MIN_SEARCH_LENGTH = 3
const meetsSearchMinLength = () =>
  searchBox.value.trim().length >= MIN_SEARCH_LENGTH

const updateSearchBtnState = () => {
  if (!searchBtn) return
  if (meetsSearchMinLength()) {
    searchBtn.removeAttribute('disabled')
  } else {
    searchBtn.setAttribute('disabled', 'true')
  }
}

const clearFeaturedArticles = () => {
  if (!isArticleHub) return
  tiles.forEach(tile => {
    tile.classList.remove('featured', 'reversed')
    tile.classList.add('small', 'forward')
  })
}

const resetFeaturedArticles = () => {
  if (!isArticleHub) return
  tiles.forEach((tile, index) => {
    if (index < 2) {
      tile.classList.remove('small')
      tile.classList.add('featured')
    }
    if (index === 1) {
      tile.classList.remove('small')
      tile.classList.add('reversed')
    }
  })
}

const handleSearch = () => {
  const val = searchBox.value.toLowerCase()
  let showList = []
  searchTerm.innerText = searchBox.value.toLowerCase()

  if (val.length >= MIN_SEARCH_LENGTH) {
    initResultsQty.classList.add('searching')
    search.classList.add('active')
    toggleFilterWrapper(false)
    // Reveal the mobile floating results count during search (matches the filter
    // flow's `.filtered` class). Desktop uses the in-page .results-found count.
    if (contentFilterMain) contentFilterMain.classList.add('searching')
    const filterList = val.split(' ')

    filterList.forEach(word => {
      tiles.forEach(tile => {
        const summary = tile.dataset.summarized.replace(/[\s]+/g, ' ')
        if (summary.indexOf(word) > -1) {
          showList = addUnique(showList, tile)
        }
      })
    })

    tiles.forEach(tile => {
      tile.classList.add('hidden')
    })

    if (showList.length > 0) {
      showList.forEach(showTile => {
        showTile.classList.remove('hidden')
      })
      updateResultsQty(showList.length)
    } else {
      tiles.forEach(showTile => {
        showTile.classList.add('hidden')
      })

      if (showList.length === 0) {
        updateResultsQty(0)
      } else {
        updateResultsQty(initResultsQty.innerText)
      }
    }

    announceStatus(
      `${showList.length} ${showList.length === 1 ? 'result' : 'results'}`
    )
    filteredList = showList.length
  } else {
    resetFeaturedArticles()
    search.classList.remove('active')
    toggleFilterWrapper(true)
    if (contentFilterMain) contentFilterMain.classList.remove('searching')
    filteredList = 0
  }

  window.scrollTo(0, 0)
}

const handleSearchBtn = () => {
  if (searchBox.value !== '') {
    search.classList.add('active')
    searchBtn.style.display = 'none'
    clearSearchBtn.style.display = 'block'
    handleSearch()
    clearFeaturedArticles()
    document.querySelector('#results-found').focus()
  } else {
    resetFeaturedArticles()
    handleSearchClearBtn()
  }
}
// Swap the search icon back in and hide the clear button — the state both
// paths below eventually settle into, just on different triggers (see
// handleSearchClearBtn and the clearSearchBtn blur listener).
const showSearchIcon = () => {
  search.classList.remove('active')
  searchBtn.style.display = 'block'
  clearSearchBtn.style.display = 'none'
}

const handleSearchClearBtn = () => {
  // Bound to the clear button's click, but handleSearchBtn also calls it with
  // no event when the box is already empty. Whether to protect focus is
  // decided by whether the browser actually gave this button focus, not by
  // guessing input modality from event.detail: Safari on macOS doesn't focus
  // a button on mouse click, but Chrome/Firefox/Edge on Windows — NVDA's
  // usual environment — do, so a plain mouse click there still has focus to
  // protect and needs the same defer-to-blur handling as keyboard/AT
  // activation.
  const hadFocus = document.activeElement === clearSearchBtn
  // iOS never gives this button real DOM focus on tap, so hadFocus is always
  // false there even though VoiceOver's cursor is typically on the button
  // mid-gesture — see the matching comment in clearFiltersBtn's handler,
  // which hits the identical bug: swapping the icon back immediately pulls
  // the element out from under VoiceOver and swallows the announcement
  // below. Treat iOS like "had focus" so it defers to the timer fallback too.
  const deferHide = hadFocus || isIOSDevice

  initResultsQty.classList.remove('searching')
  if (contentFilterMain) contentFilterMain.classList.remove('searching')
  searchBox.value = ''
  searchTerm.innerText = ''
  updateSearchBtnState()
  resetList(tiles)
  toggleFilterWrapper(true)
  updateResultsQty(initResultsQty.innerText)
  resetFeaturedArticles()
  document.querySelector('.content-filtering').classList.remove('active')

  // Swapping the icons back is deferred to the button's own blur (below)
  // rather than done here immediately, only when the button is focused:
  // hiding a focused element forces the browser to move focus elsewhere on
  // its own (typically <body>), and that focus-change announcement was
  // competing with — and beating — the "Search cleared" message below (heard
  // as a spurious "blank"). When the button was never focused, there's no
  // focus to protect, so it swaps the icon back immediately.
  if (!deferHide) showSearchIcon()

  announceStatus(`Search cleared. ${currentResultsText()}`)

  if (deferHide) {
    // No-ops on non-iOS (hadFocus-only case), which relies on the blur
    // listener below instead.
    scheduleIOSHideFallback(() => {
      if (searchBox.value === '') showSearchIcon()
    })
  }
}

if (searchBox) {
  // Disabled by default; only enabled once the minimum length is reached
  updateSearchBtnState()

  searchBox.addEventListener('input', () => {
    // Leave the tags/filters enabled while typing; they're only disabled once
    // a search is actually executed (via handleSearch on button click / Enter).
    updateSearchBtnState()
  })

  searchBox.addEventListener('blur', () => {
    if (searchBox.value === '' && filteredList === 0) {
      toggleFilterWrapper(true)
    }
  })

  searchBox.addEventListener('keyup', event => {
    if (event.key === 'Enter' && meetsSearchMinLength()) {
      handleSearchBtn()
    }
  })
  searchBtn.addEventListener('click', handleSearchBtn)
  clearSearchBtn.addEventListener('click', handleSearchClearBtn)

  // Finishes the icon swap that handleSearchClearBtn intentionally deferred:
  // once focus has genuinely moved off this button, it's safe to hide it
  // without forcing an unwanted focus change. Guarded on an empty field so
  // tabbing past the button while there's still real text to clear (it never
  // got activated) leaves the "X" showing, as it should.
  clearSearchBtn.addEventListener('blur', () => {
    if (searchBox.value === '') showSearchIcon()
  })
}

// Function to get query string value and sanitize it
function getQueryStringValue(key) {
  const urlParams = new URLSearchParams(window.location.search)
  const value = urlParams.get(key)
  return value ? sanitizeInput(value) : null
}

// Safari's UA also contains "Safari", so Chrome/Edge/etc. have to be excluded
// explicitly to tell them apart. Used only to gate the deep-link filter focus
// move below — a real engine-level VoiceOver compatibility split, not a
// general-purpose browser check.
const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent
)

const initContentFilter = ({
  mainSelector,
  modalSelector,
  tilePrefix,
  queryParam,
  tagWrapperSelector,
  onFilterApplied,
  onFilterCleared,
  toggleSearchOnFilter,
  // What the clear-all control announces. Each archive calls its filters
  // something different in the UI ("Clear tags" vs "Clear filters"), and the
  // announcement should match what the user just pressed.
  clearedLabel
}) => {
  const contentLibrary = document.querySelector(mainSelector)
  const modal = document.querySelector(modalSelector)
  if (modal) {
    const filterPanelMql = window.matchMedia('(min-width: 992px)')
    filterPanel = createOverlayPanel({
      container: modal,
      toggleTarget: content,
      openClass: 'mobile-filter-menu-open',
      isActiveBreakpoint: () => !filterPanelMql.matches,
      setInert: setMobileFilterMenuBackgroundHidden,
      wrapForwardTo: modal.querySelector('.filter-title'),
      // Land on the panel's heading (#filter-by-tag / #filter-by-topic)
      // rather than the close button, so screen reader users hear what the
      // panel is before reaching its controls. The heading isn't natively
      // focusable — see the tabindex="-1" on it in the archive templates.
      focusOnOpen: () =>
        modal.querySelector('.filter-title') || mobileFilterCloseBtn,
      focusDelay: 200,
      restoreFocusTo: () => mobileFilterOpenBtn,
      onBeforeOpen: () => {
        mobileFilterOpenBtn.setAttribute('aria-expanded', 'true')
        mobileFilterCloseBtn.setAttribute('aria-expanded', 'true')
        if (navPanel.isOpen()) navPanel.close({ restoreFocus: false })
      },
      onAfterClose: () => {
        mobileFilterOpenBtn.setAttribute('aria-expanded', 'false')
        mobileFilterCloseBtn.setAttribute('aria-expanded', 'false')
      }
    })
  }

  const resultsList = document.querySelector('#results-list')
  const resultsListBaseLabel = resultsList
    ? resultsList.getAttribute('aria-label')
    : null

  // A filter's human name. Article-hub pills carry it in aria-label ("Campaign
  // Planning filter"); research-center checkboxes rely on their visible <label>.
  const filterDisplayName = input => {
    const fromAria = input.getAttribute('aria-label')
    if (fromAria) return fromAria.replace(/\s*filter$/i, '')
    const label = document.querySelector(`label[for="${input.id}"]`)
    const text = label ? label.textContent.trim().replace(/\s+/g, ' ') : ''
    return text || input.id
  }

  const activeFilters = () => Array.from(filters).filter(isFilterActive)

  // "25 results filtered by Direct Mail Tactics, Security", or just the count when
  // nothing is selected. A pill announces its own selected state, but that only
  // covers the one just pressed — with several active, the count alone leaves the
  // user to remember what it is a count of. One phrasing is shared by the
  // announcements and the results list's accessible name so the two agree.
  const resultsWithFiltersText = () => {
    const active = activeFilters()
    const count = currentResultsText()
    if (!active.length) return count
    return `${count} filtered by ${active.map(filterDisplayName).join(', ')}`
  }

  // Keep the results list's accessible name describing what it currently holds.
  // This is the only place a screen reader reliably picks the filter state up on
  // a deep-link landing: VoiceOver ignores live-region updates that fire during
  // page load, and it does not follow a programmatic focus into a tabindex="-1"
  // container while it is reading the fresh document. The name is read whenever
  // the user reaches or focuses the list, load or not.
  const syncResultsListLabel = () => {
    if (!resultsList || !resultsListBaseLabel) return

    resultsList.setAttribute(
      'aria-label',
      activeFilters().length
        ? `${resultsListBaseLabel}, ${resultsWithFiltersText()}`
        : resultsListBaseLabel
    )
  }

  // Note: no wrapper-level Enter handler. The native checkbox is the single focus
  // target; Space toggles it natively and the per-checkbox keyup handler below adds
  // Enter support. (The old label had tabindex="0" + this handler, which caused a
  // double focus stop and double-fired the toggle when the input itself was focused.)

  let filtersChecked = []
  let filterValue = null
  // Set for the duration of the clearFiltersBtn click handler below so
  // updateContentList leaves the button visible — it stays focused and
  // announces "cleared" itself, then hides on its own blur, the same pattern
  // clearSearchBtn uses.
  let deferClearBtnHide = false

  // Article-hub filter pills are real <button role="switch" aria-checked> toggles.
  // aria-pressed was tried first, but VoiceOver's interaction hint for a
  // button+aria-pressed control is unreliable — it can announce "checkbox"
  // wording regardless of the actual role. role="switch" has its own dedicated
  // platform mapping and avoids that shared hint pool. Before that, this was a
  // native <input type="checkbox" role="button">, which had a worse version of
  // the same problem (native checked state alongside the ARIA override).
  // Research-center still uses plain checkboxes. These two helpers read/write
  // "is this filter active" across both without the rest of the code needing to
  // know which kind of control it's looking at.
  const isFilterActive = el =>
    el.hasAttribute('aria-checked')
      ? el.getAttribute('aria-checked') === 'true'
      : el.checked

  const setFilterActive = (el, active) => {
    if (el.hasAttribute('aria-checked')) {
      el.setAttribute('aria-checked', active ? 'true' : 'false')
    } else {
      el.checked = active
    }
  }

  const updateContentList = () => {
    const showList = []
    const showFilters = []

    tiles.forEach(tile => {
      tile.classList.forEach(className => {
        if (className.indexOf(tilePrefix) > -1) {
          const filter = className.replace(tilePrefix, '')
          if (filtersChecked.includes(filter)) {
            if (!showFilters.includes(filter)) showFilters.push(filter)
            if (!showList.includes(tile)) showList.push(tile)
          }
        }
      })
      tile.classList.add('hidden')
    })

    filterLabels.forEach(filter => {
      filter.classList.toggle(
        'unselected',
        !showFilters.includes(filter.dataset.filter)
      )
    })

    if (showList.length > 0) {
      contentLibrary.classList.add('filtered')
      disableSearchBox()
      showList.forEach(showTile => showTile.classList.remove('hidden'))
      clearFiltersBtn.style.display = 'flex'
      updateResultsQty(showList.length)
      if (onFilterApplied) onFilterApplied()
    } else {
      contentLibrary.classList.remove('filtered')
      enableSearchBox()
      tiles.forEach(showTile => showTile.classList.remove('hidden'))
      // This fires on *any* transition to zero active filters — the Clear
      // button's own click handler (which sets deferClearBtnHide to protect
      // desktop focus) and a regular filter pill toggling off the last
      // active filter (which never touched deferClearBtnHide at all) both
      // land here. On iOS, hiding the button synchronously pulls it out of
      // the accessibility tree mid-gesture and was interrupting whatever
      // VoiceOver announcement fires right after — reproduced both as the
      // "Clear tags" announcement going missing and, separately, as
      // toggling the last tag off announcing only "unselected" and cutting
      // off the results count that should follow. Deferring here, once, for
      // every trigger, replaces the Clear-button-only fallback scheduling
      // that used to live in its click handler.
      if (deferClearBtnHide) {
        // Desktop hadFocus case: left alone, hides itself via the blur
        // listener once focus genuinely leaves.
      } else if (isIOSDevice) {
        scheduleIOSHideFallback(() => {
          if (filtersChecked.length === 0)
            clearFiltersBtn.style.display = 'none'
        })
      } else {
        clearFiltersBtn.style.display = 'none'
      }
      updateResultsQty(initResultsQty.innerText)
      if (onFilterCleared) onFilterCleared()
    }

    // After updateResultsQty, so the count in the name is the new one.
    syncResultsListLabel()

    window.scrollTo(0, 0)
  }

  const handleFilterChange = event => {
    let target = event.target
    const fromQuery = event.type !== 'change' && event.type !== 'click'

    if (fromQuery) {
      target = document.querySelector(`#${filterValue}`)
      if (!target) return
      setFilterActive(target, true)
    } else if (target.hasAttribute('aria-checked')) {
      // Buttons have no native toggle to react to — flip it ourselves.
      // Checkboxes are already flipped by the browser before 'change' fires.
      setFilterActive(target, !isFilterActive(target))
    }

    filtersChecked = addOrRemove(filtersChecked, target.name)

    if (tagWrapperSelector) {
      const parent = target.closest(tagWrapperSelector)
      parent.classList.toggle(
        'active-filter',
        isFilterActive(target) || fromQuery
      )
    }

    if (toggleSearchOnFilter) {
      if (filtersChecked.length > 0) {
        disableSearchBox()
      } else {
        enableSearchBox()
      }
    }

    updateContentList()

    // A query-param landing announces once from announceQueryFilterResults below,
    // after the repeated init passes settle — otherwise the bare count would fire
    // first and be immediately followed by the fuller message.
    if (fromQuery) return

    // Announce the outcome via the live region, naming every filter still applied
    // so the count is never reported without saying what it counts. For a
    // switch pill switching OFF, say "unselected" explicitly first —
    // VoiceOver often stays silent when a toggle turns off.
    const results = resultsWithFiltersText()
    if (target.hasAttribute('aria-checked') && !isFilterActive(target)) {
      announceStatus(
        `${filterDisplayName(target)} filter unselected, ${results}`
      )
    } else {
      announceStatus(results)
    }
  }

  filters.forEach(filter => {
    const isButton = filter.hasAttribute('aria-checked')
    // Buttons fire 'click' on activation and never 'change'; checkboxes are the
    // reverse — 'change' fires after the browser has already flipped .checked.
    filter.addEventListener(isButton ? 'click' : 'change', handleFilterChange)
    const preventArrowDefault = event => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()
      }
    }
    filter.addEventListener('keydown', preventArrowDefault)
    // Real <button> elements already activate on both Enter and Space natively —
    // forwarding .click() here too would fire the toggle twice. Checkboxes only
    // get Space for free, so Enter needs this extra push.
    if (!isButton) {
      filter.addEventListener('keyup', event => {
        if (event.key === 'Enter') {
          event.target.click()
        } else {
          preventArrowDefault(event)
        }
      })
    }
  })

  // Clear any browser-restored checkbox state (back-nav / bfcache) so the
  // visible UI matches our JS state (`filtersChecked` is empty on re-init).
  // Chrome restores checkbox state on back-navigation after DOMContentLoaded,
  // sometimes after pageshow too — run once on pageshow and again on the
  // next frame to beat the autofill timing.
  const resetFilterState = () => {
    filters.forEach(filter => {
      setFilterActive(filter, false)
      if (tagWrapperSelector) {
        const wrapper = filter.closest(tagWrapperSelector)
        if (wrapper) wrapper.classList.remove('active-filter')
      }
    })
    filtersChecked = []
    enableSearchBox()
    updateContentList()
    const contentFiltering = document.querySelector('.content-filtering')
    if (contentFiltering) contentFiltering.classList.remove('active')
    clearFiltersBtn.style.display = 'none'
  }

  // Landing on a deep link (?filter_tags=campaign-planning) should leave the page
  // in the same state as picking that tag on the page: results filtered and the
  // outcome announced. The filter itself is applied by applyQueryParamFilter on
  // several passes (init / rAF / pageshow / load) to beat browser checkbox
  // restoration, so this settles first and then runs once.
  //
  // This also moves focus into the results list, but only outside Safari (see
  // isSafariBrowser) and well after the announcement above. NVDA/JAWS need the
  // focus move to pick up the list's name at all; VoiceOver+Chrome needs a
  // focus-driven accessibility event to notice either the live region or the
  // aria-label change (Chrome's AX bridge to VoiceOver is known to drop
  // notifications that aren't tied to focus). VoiceOver+Safari is the one
  // combination that gets worse with it — Safari's native integration doesn't
  // have Chrome's gap, and moving focus at all, even a full second in, fights
  // VoiceOver's own start-of-document reading and buries the announcement — so
  // it's the one browser this is skipped for.
  let queryFilterHandled = false
  let queryFilterSettleTimer
  let queryFilterFocusHandled = false
  let queryFilterFocusTimer

  const announceQueryFilterResults = () => {
    if (queryFilterHandled) return

    const slug = filterValue ? filterValue.split(',')[0] : null
    const input = slug ? document.getElementById(slug) : null
    if (!input || !isFilterActive(input)) return

    queryFilterHandled = true

    // Name the tag here — on a deep link the pill never takes focus, so this is
    // the part a screen reader would otherwise get from the control itself.
    announceStatus(
      `${filterDisplayName(input)} filter selected. ${currentResultsText()}`
    )
  }

  const scheduleQueryFilterAnnouncement = () => {
    if (queryFilterHandled) return
    clearTimeout(queryFilterSettleTimer)
    // 250ms landed this right in the middle of VoiceOver+Safari's own
    // start-of-document reading often enough to get swallowed by it (QA:
    // "results vary"). Giving it more headroom before competing with that
    // native reading is a timing guess pending AT retest, not a confirmed
    // fix — see the focus delay below, which was pushed out to match so the
    // focus move still lands "well after" this announcement per the intent
    // described above.
    queryFilterSettleTimer = setTimeout(announceQueryFilterResults, 500)
  }

  const focusQueryFilterResults = () => {
    if (queryFilterFocusHandled) return

    const slug = filterValue ? filterValue.split(',')[0] : null
    const input = slug ? document.getElementById(slug) : null
    if (!input || !isFilterActive(input) || !resultsList) return

    // Only claim focus while the page still holds it. The "Skip to results" link
    // lands on the list itself and #results-found sits right above it, so both
    // count as untouched; so does the header's "Skip to main content" link
    // (#skip a) — VoiceOver+Chrome moves real DOM focus onto it just by
    // reading through the page from the top, before the user has done
    // anything, and that was being read as "someone is already interacting"
    // and silently blocking this focus move — which is the only thing that
    // gets Chrome's AX bridge to notice the live region at all (see
    // scheduleQueryFilterFocus below). Anything else really does mean
    // someone is interacting, and moving focus out from under them a full
    // second in would be worse than staying put.
    const active = document.activeElement
    const resultsFound = document.querySelector('#results-found')
    const skipLink = document.querySelector('#skip a')
    const pageStillHoldsFocus =
      !active ||
      active === document.body ||
      active === document.documentElement ||
      active === resultsList ||
      active === skipLink ||
      (resultsFound && resultsFound.contains(active))
    if (!pageStillHoldsFocus) return

    queryFilterFocusHandled = true
    resultsList.focus({ preventScroll: true })
  }

  const scheduleQueryFilterFocus = () => {
    if (queryFilterFocusHandled || isSafariBrowser) return
    clearTimeout(queryFilterFocusTimer)
    // 1000ms landed mid-way through NVDA's own page-title announcement often
    // enough that the forced focus move cut it off (QA: "begins reading AH
    // page title, cuts off w/ long pause, then reads filter selected...").
    // Pushed out to give that native announcement more room to finish before
    // this interrupts it — a timing guess pending AT retest, not a confirmed
    // fix.
    queryFilterFocusTimer = setTimeout(focusQueryFilterResults, 1500)
  }

  const applyQueryParamFilter = () => {
    filterValue = getQueryStringValue(queryParam)
    if (filterValue) {
      handleFilterChange({ target: { name: filterValue.split(',')[0] } })
      scheduleQueryFilterAnnouncement()
      scheduleQueryFilterFocus()
    }
  }

  const resetAndApply = () => {
    resetFilterState()
    applyQueryParamFilter()
  }

  resetAndApply()
  requestAnimationFrame(() => resetAndApply())
  window.addEventListener('pageshow', () => {
    resetAndApply()
    requestAnimationFrame(() => resetAndApply())
  })
  window.addEventListener('load', () => resetAndApply())

  clearFiltersBtn.addEventListener('click', () => {
    const allFilters = document.querySelectorAll('.filters .form-check-input')

    if (searchBox) searchBox.value = ''
    if (searchTerm) searchTerm.innerText = ''
    if (search) search.classList.remove('active')
    if (searchBtn) searchBtn.style.display = 'block'
    if (clearSearchBtn) clearSearchBtn.style.display = 'none'
    if (searchBox) updateSearchBtnState()
    filtersChecked = []

    // Whether to protect focus is decided by whether the browser actually
    // gave this button focus, not by guessing input modality from
    // event.detail: Safari on macOS doesn't focus a button on mouse click,
    // but Chrome/Firefox/Edge on Windows — NVDA's usual environment — do, so
    // a plain mouse click there still has focus to protect and needs the
    // same defer-until-blur handling as keyboard/AT activation. iOS doesn't
    // need a case here: updateContentList itself now defers the hide for
    // every iOS trigger, not just this one, so this flag only has to cover
    // the desktop hadFocus case.
    const hadFocus = document.activeElement === clearFiltersBtn

    // Same trick as handleSearchClearBtn/clearSearchBtn: keep the button visible
    // and focused through updateContentList rather than letting it vanish
    // mid-handler. Hiding a focused element forces the browser to reassign focus
    // on its own (it falls back to <body>), and that focus-change announcement
    // was competing with — and beating — the "Filters cleared" message below
    // (heard as a spurious "blank"). The button hides itself once focus
    // genuinely leaves, via the blur listener below.
    deferClearBtnHide = hadFocus
    updateContentList()
    deferClearBtnHide = false

    allFilters.forEach(filter => {
      setFilterActive(filter, false)
      if (tagWrapperSelector) {
        const parent = filter.closest(tagWrapperSelector)
        if (parent) parent.classList.remove('active-filter')
      }
    })

    // Again, now that the boxes are actually unchecked — the updateContentList
    // above ran while they still read as checked, so its name was the old one.
    syncResultsListLabel()

    document.querySelector('.content-filtering').classList.remove('active')
    clearQueryStringParameter(queryParam)

    announceStatus(`${clearedLabel}, ${currentResultsText()}`)
  })

  // Finishes what the click handler above deferred: once focus has genuinely
  // moved off this button, it's safe to hide it without forcing an unwanted
  // focus change. Guarded on filtersChecked so re-selecting a filter before
  // blurring away (it never actually cleared) leaves the button showing.
  clearFiltersBtn.addEventListener('blur', () => {
    if (filtersChecked.length === 0) clearFiltersBtn.style.display = 'none'
  })
}

if (document.body.classList.contains('post-type-archive-dtw_articles')) {
  initContentFilter({
    mainSelector: 'main.research-center',
    modalSelector: '.research-center-filters',
    tilePrefix: 'dtw_filters-',
    queryParam: 'filter_topics',
    clearedLabel: 'Filters cleared'
  })
}

if (document.body.classList.contains('post-type-archive-article-hub')) {
  initContentFilter({
    mainSelector: 'main.article-hub',
    modalSelector: '.article-hub-filters',
    tilePrefix: 'article_tags-',
    queryParam: 'filter_tags',
    tagWrapperSelector: '.article-tag',
    toggleSearchOnFilter: true,
    onFilterApplied: clearFeaturedArticles,
    onFilterCleared: resetFeaturedArticles,
    clearedLabel: 'Tags cleared'
  })
}

// carousel init
function handleCarouselLinks(slides, currentIndex) {
  slides.forEach((slide, index) => {
    const links = slide.querySelectorAll('a')
    const isActive = index === currentIndex
    links.forEach(link => {
      if (isActive) {
        link.removeAttribute('tabindex')
        link.setAttribute('aria-hidden', 'false')
      } else {
        link.setAttribute('tabindex', '-1')
        link.setAttribute('aria-hidden', 'true')
      }
    })
  })
}

if (document.querySelector('.fifty-fifty-carousel')) {
  jQuery(() => {
    const unslider = jQuery('.banner')
      .unslider({
        autoplay: false,
        arrows: false,
        speed: 300
      })
      .css('overflow', 'clip visible')

    const handleDots = () => {
      jQuery('.unslider-nav li').each(function (index) {
        const active = jQuery(this).hasClass('unslider-active')
        if (active) {
          jQuery(this).attr('tabindex', '-1').attr('aria-label', '')
        } else {
          jQuery(this)
            .attr('tabindex', '0')
            .attr('aria-label', `go to slide ${index + 1}`)
        }
      })
    }
    const allSlides = document.querySelectorAll('.unslider-carousel li')
    handleCarouselLinks(allSlides, 0)

    unslider.on('unslider.change', (event, index, slide) => {
      const idx = parseInt(index, 10)
      handleDots()
      handleCarouselLinks(allSlides, idx)
      setTimeout(() => {
        slide.focus()
      }, 350)
    })

    jQuery('.unslider-nav li').keyup(function (e) {
      if (e.keyCode === 13) jQuery(this).click()
    })

    jQuery('.unslider-arrow')
      .keyup(e => {
        e.preventDefault()
      })
      .click(function (e) {
        e.preventDefault()
        const fn = this.className.split(' ')[1]

        unslider.data('unslider')[fn]()
      })

    handleDots()
  })
}

function originationPassthrough() {
  const links = document.querySelectorAll('a[href]')
  links.forEach(link => {
    if (
      link.href.indexOf('prodpx-promotool.usps.com') !== -1 &&
      link.href.indexOf('origination') === -1
    ) {
      link.href += `&origination=${sessionStorage.origination}`
    }
  })
}

// If origination is in URL, persist it for contact form links
const origination =
  getQueryStringValue('origination') || getQueryStringValue('iom')
if (origination) {
  sessionStorage.origination = origination
}

// If origination is in session storage (URL or an earlier page this visit)
// pass it to contact form links
if (sessionStorage.origination) {
  originationPassthrough()
}

// Global object to keep track of processed footnotes
const processedFootnotes = {}
const footnoteRefCounters = {}
let footnoteCounter = 0

const wrapRegisteredMarks = content => {
  if (!content || !content.includes('®')) {
    return content
  }

  const container = document.createElement('span')
  container.innerHTML = content
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  const textNodes = []

  let currentNode = walker.nextNode()
  while (currentNode) {
    textNodes.push(currentNode)
    currentNode = walker.nextNode()
  }

  textNodes.forEach(textNode => {
    if (!textNode.nodeValue || !textNode.nodeValue.includes('®')) {
      return
    }
    if (textNode.parentElement && textNode.parentElement.closest('sup')) {
      return
    }

    const parts = textNode.nodeValue.split('®')
    const fragment = document.createDocumentFragment()

    parts.forEach((part, index) => {
      if (part) {
        fragment.appendChild(document.createTextNode(part))
      }
      if (index < parts.length - 1) {
        const sup = document.createElement('sup')
        sup.textContent = '®'
        fragment.appendChild(sup)
      }
    })

    textNode.parentNode.replaceChild(fragment, textNode)
  })

  return container.innerHTML
}

// Mark superscripts that contain registered or trademark marks for downstream styling/a11y
const markRegisteredSuperscripts = () => {
  if (getQueryStringValue('debug') !== 'footnotes') {
    return
  }

  const body = document.querySelector('body')
  body.classList.add('debug-footnotes')

  const superscripts = document.querySelectorAll('sup')
  superscripts.forEach(sup => {
    const text = sup.textContent || ''
    const html = sup.innerHTML || ''
    const htmlLower = html.toLowerCase()

    if (text.includes('®') || htmlLower.includes('&reg;')) {
      sup.setAttribute('data-regmark', 'true')
    }

    if (text.includes('™') || htmlLower.includes('&trade;')) {
      sup.setAttribute('data-trademark', 'true')
    }
  })
}

function processFootnote(supElement) {
  if (supElement.tagName.toLowerCase() !== 'sup') {
    console.error('Element is not a SUP element:', supElement)
    return
  }

  const footnote = supElement.dataset.footnote
  // Use textContent (not innerText) so markers inside closed <details> are still readable
  const footnoteMarker = supElement.textContent

  // Check if the footnote has been processed before
  if (Object.hasOwn(processedFootnotes, footnoteMarker)) {
    const existingFootnoteIndex = processedFootnotes[footnoteMarker]
    createSupLink(supElement, existingFootnoteIndex)
  } else {
    const newFootnoteIndex = footnoteCounter++
    processedFootnotes[footnoteMarker] = newFootnoteIndex
    createFootnoteItem(footnote, footnoteMarker, newFootnoteIndex)
    createSupLink(supElement, newFootnoteIndex)
  }
}

// Scrolls targetEl into view only if it isn't already fully visible, then
// focuses it. Shared by the footnote backlink/ref click handlers and the
// hash-on-load anchor restore below.
const scrollIntoViewIfNeededThenFocus = (targetEl, yOffset = -100) => {
  const rect = targetEl.getBoundingClientRect()
  const isInView =
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth

  if (!isInView) {
    window.scrollTo({
      top: rect.top + window.pageYOffset + yOffset,
      behavior: 'smooth'
    })
  }

  targetEl.focus()
}

function createFootnoteItem(footnote, _footnoteMarker, index) {
  const indexNumber = index + 1
  const item = document.createElement('li')

  // Create a new a element
  const span = document.createElement('span')
  const marker = document.createElement('i')
  const linkBack = document.createElement('a')

  // Add ID to span for accessibility
  span.id = `footnote-text-${indexNumber}`

  linkBack.innerText = '↩'
  linkBack.href = `#footnote-label-${indexNumber}-1`
  linkBack.setAttribute('aria-label', 'Back to content')
  linkBack.setAttribute('role', 'doc-backlink')
  linkBack.setAttribute('tabindex', '0')
  linkBack.classList.add('footnote-backref')

  linkBack.addEventListener('click', e => {
    e.preventDefault()
    const targetId =
      (linkBack.getAttribute('href') || '').replace('#', '') ||
      `footnote-label-${indexNumber}-1`
    const targetEl = document.getElementById(targetId)
    if (targetEl) {
      location.hash = `#${targetId}`

      setTimeout(() => scrollIntoViewIfNeededThenFocus(targetEl), 300)
    }
  })

  span.innerHTML = wrapRegisteredMarks(footnote)

  item.id = `footnote-${indexNumber}`
  item.setAttribute('tabindex', '-1')

  item.style.order = indexNumber

  marker.innerText = `${indexNumber}.`
  marker.setAttribute('aria-label', `footnote ${indexNumber}`)
  item.appendChild(marker)
  item.appendChild(span)
  item.appendChild(linkBack)

  const footnoteList = document.querySelector('#footnotes .footnote-list')
  footnoteList.appendChild(item)

  const footnoteWrapper = document.querySelector('#footnotes')
  footnoteWrapper.classList.add('has-notes')
}

function createSupLink(supElement, index) {
  const indexNumber = index + 1
  const refIndex = (footnoteRefCounters[indexNumber] || 0) + 1
  footnoteRefCounters[indexNumber] = refIndex
  const supRefId = `footnote-label-${indexNumber}-${refIndex}`
  const supLink = document.createElement('a')
  supLink.href = `#footnote-${indexNumber}`
  supLink.id = supRefId
  supLink.setAttribute('aria-label', `go to footnote ${indexNumber}`)
  supLink.setAttribute('role', 'doc-noteref')
  supLink.classList.add('footnote-ref')

  supLink.addEventListener('click', e => {
    e.preventDefault()
    const footnoteEl = document.getElementById(`footnote-${indexNumber}`)
    if (footnoteEl) {
      const backref = footnoteEl.querySelector('.footnote-backref')
      if (backref) {
        backref.setAttribute('href', `#${supRefId}`)
        backref.setAttribute('aria-label', `Back to citation ${indexNumber}`)
      }
    }

    const targetEl = document.getElementById(`footnote-${indexNumber}`)
    if (targetEl) {
      location.hash = `footnote-${indexNumber}`

      setTimeout(() => scrollIntoViewIfNeededThenFocus(targetEl), 300)
    }
  })

  while (supElement.firstChild) {
    supLink.appendChild(supElement.firstChild)
  }

  supElement.appendChild(supLink)
}

// Add this function to preserve footnote anchor when page loads with a hash
function preserveFootnoteAnchor() {
  const hash = window.location.hash
  if (
    hash &&
    (hash.includes('footnote-') || hash.includes('footnote-label-'))
  ) {
    setTimeout(() => {
      const targetEl = document.querySelector(hash)
      if (targetEl) {
        // Let the browser's native scrolling behavior work with CSS scroll-margin
        // Only adjust if needed after a delay
        setTimeout(() => scrollIntoViewIfNeededThenFocus(targetEl), 200)
      }
    }, 100)
  }
}

function processAllFootnotes() {
  const supMissingData = document.querySelectorAll(
    'sup:not([data-footnote],[data-regmark])'
  )
  supMissingData.forEach(sup => {
    const supText = sup.textContent
    if (supText === '&reg;' || supText === '®') {
      sup.dataset.regmark = true
    } else if (!supText.trim()) {
      console.warn('Found a footnote marker with no content:', supText)
    }
  })
  const supElements = document.querySelectorAll('sup[data-footnote]')
  const footnotesSection = document.querySelector('#footnotes')
  const articleExtras = document.querySelector('#footnotes .article-extras')
  const footnoteLabel = document.querySelector('#footnote-label')

  if (supElements.length > 0 || articleExtras) {
    if (footnotesSection) {
      footnotesSection.setAttribute('aria-hidden', 'false')
      // `.has-notes` controls visibility (CSS hides #footnotes:not(.has-notes)).
      // Set it here so pages with only article-extras (no <sup> markers) still show.
      footnotesSection.classList.add('has-notes')
    }
  }

  if (footnoteLabel && supElements.length === 0) {
    footnoteLabel.setAttribute('aria-hidden', 'true')
  }

  supElements.forEach(processFootnote)

  // Handle initial hash if present
  preserveFootnoteAnchor()
}

// When landing on an anchor with data-focus-target, move focus to its next sibling
const focusHashAnchors = () => {
  const { hash } = window.location
  if (!hash || hash.length <= 1) {
    return
  }

  const hashValue = decodeURIComponent(hash.slice(1))
  const anchors = document.querySelectorAll('a[data-focus-target]')

  anchors.forEach(anchor => {
    const anchorId = anchor.id || anchor.getAttribute('id')
    const focusTargetId = anchor.dataset.focusTarget

    if (!anchorId || !focusTargetId) {
      return
    }

    if (hashValue !== anchorId && !hashValue.includes(anchorId)) {
      return
    }

    const target = anchor.nextElementSibling
    if (!target) {
      return
    }

    if (target.id !== focusTargetId) {
      target.id = focusTargetId
    }

    target.setAttribute('tabindex', '-1')

    const applyFocus = () => {
      if (document.activeElement === target) {
        return
      }

      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      if (typeof target.focus === 'function') {
        target.focus({ preventScroll: true })
      }
    }

    // Run focus twice to survive screen reader auto-focus on skip links
    window.requestAnimationFrame(() => {
      window.setTimeout(applyFocus, 80)
      window.setTimeout(applyFocus, 600)
    })
  })
}

document.addEventListener('DOMContentLoaded', focusHashAnchors)
window.addEventListener('pageshow', focusHashAnchors)
document.addEventListener('DOMContentLoaded', markRegisteredSuperscripts)

// Add a handler to prevent other scroll triggers from removing our footnote hash
window.addEventListener(
  'scroll',
  function () {
    const hash = window.location.hash
    if (
      hash &&
      (hash.includes('footnote-') || hash.includes('footnote-label-'))
    ) {
      // Use a throttled approach to not interfere with normal scrolling
      if (!this.isScrollingFootnote) {
        this.isScrollingFootnote = true
        setTimeout(() => {
          // Check if hash was changed during scroll
          if (window.location.hash !== hash) {
            // Force the hash back to what it was
            history.pushState(null, null, hash)
          }
          this.isScrollingFootnote = false
        }, 100)
      }
    }
  },
  { passive: true }
)

// Intercept any attempts to clear the hash when a footnote is active
window.addEventListener(
  'hashchange',
  function (e) {
    const oldHash = new URL(e.oldURL).hash
    const newHash = new URL(e.newURL).hash

    // If we're navigating from a footnote to an empty hash, and it wasn't a user clicking a link
    // (likely caused by the sticky nav), prevent the change
    if (
      (oldHash.includes('footnote-') || oldHash.includes('footnote-label-')) &&
      !newHash
    ) {
      // Check if this wasn't triggered by user clicking a non-footnote link
      if (!this.isUserNavigating) {
        history.pushState(null, null, oldHash)
      }
    }
  },
  false
)

// Track activation on actual links so keyboard/AT clicks still work without
// making the entire document a clickable ancestor of its text.
document.querySelectorAll('a[href]').forEach(link => {
  link.addEventListener('click', event => {
    const href = event.currentTarget.getAttribute('href')
    if (!href || href.includes('footnote-')) {
      return
    }

    window.isUserNavigating = true
    setTimeout(() => {
      window.isUserNavigating = false
    }, 100)
  })
})

document.addEventListener('DOMContentLoaded', processAllFootnotes)

/**
 * Hero with CTA — jump link smooth scroll
 */
document.addEventListener('DOMContentLoaded', function () {
  const ctaButton = document.getElementById('hero-cta-button')

  if (ctaButton) {
    const focusAfterScroll = targetEl => {
      const focusEl = targetEl.nextElementSibling || targetEl

      focusEl.setAttribute('tabindex', '-1')
      focusEl.focus({
        preventScroll: true
      })
    }

    const scrollToTarget = targetEl => {
      if (window.gsap && window.ScrollToPlugin) {
        gsap.registerPlugin(ScrollToPlugin)
        gsap.to(window, {
          duration: 0.8,
          scrollTo: {
            y: targetEl,
            offsetY: 90
          },
          autoKill: true,
          ease: 'power2.inOut',
          onComplete: () => {
            focusAfterScroll(targetEl)
          }
        })
        return
      }

      targetEl.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
      focusAfterScroll(targetEl)
    }

    const handleCtaActivate = function (event) {
      const targetHref = ctaButton.getAttribute('href')
      let targetHash = null

      if (!targetHref) {
        return
      }

      if (targetHref.startsWith('#')) {
        targetHash = targetHref
      } else {
        try {
          const hrefUrl = new URL(targetHref, window.location.href)

          if (
            hrefUrl.origin !== window.location.origin ||
            hrefUrl.pathname !== window.location.pathname ||
            !hrefUrl.hash
          ) {
            return
          }

          targetHash = hrefUrl.hash
        } catch (error) {
          console.error('Invalid URL in CTA href:', targetHref, error)
          return
        }
      }

      if (!targetHash || !targetHash.startsWith('#')) {
        return
      }

      event.preventDefault()

      const targetEl = document.querySelector(targetHash)

      if (!targetEl) {
        return
      }

      scrollToTarget(targetEl)
    }

    ctaButton.addEventListener('click', handleCtaActivate)
    ctaButton.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') {
        return
      }

      handleCtaActivate(event)
    })
  }
})

/**
 * Video inline — poster click, iframe swap, transcript toggle
 */
document.addEventListener('DOMContentLoaded', () => {
  window.addEventListener('beforeunload', () => {
    document
      .querySelectorAll('.video-inline-content iframe')
      .forEach(iframe => {
        iframe.removeAttribute('src')
        iframe.style.display = 'none'
      })
    document.querySelectorAll('.video-poster').forEach(poster => {
      poster.style.display = ''
    })
  })

  const handlePosterActivate = event => {
    if (event.type === 'keydown' && event.key !== 'Enter') {
      return
    }
    event.currentTarget.style.display = 'none'
    const iframe = document.querySelector('.video-inline-content iframe')
    if (iframe) {
      iframe.style.display = ''
      iframe.setAttribute('src', iframe.dataset.youtubeId)
    }
  }

  document.querySelectorAll('.video-poster').forEach(poster => {
    poster.addEventListener('click', handlePosterActivate)
    poster.addEventListener('keydown', handlePosterActivate)
  })

  const handleTranscriptToggle = event => {
    // Native <button> fires click for both Enter and Space, so a click handler
    // is enough. preventDefault guards against the default submit behavior of a
    // typeless <button>.
    event.preventDefault()
    const toggle = event.currentTarget
    // Only toggle the transcript belonging to the clicked control, not every
    // video block on the page.
    const inline = toggle.closest('.video-inline')
    if (inline) inline.classList.toggle('video-inline-transcript-collapse')
    const modalInner = toggle.closest('.article_video_modal_inner')
    if (modalInner) {
      modalInner.classList.toggle('video-modal-transcript-collapse')
    }
    const expanded = toggle.getAttribute('aria-expanded') === 'true'
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true')
  }

  document.querySelectorAll('.transcript-toggle').forEach(toggle => {
    toggle.addEventListener('click', handleTranscriptToggle)
  })
})

/**
 * Video three column — modal open/close
 */
document.addEventListener('DOMContentLoaded', () => {
  const videoModal = document.querySelector('dialog#video-modal')
  if (!videoModal) {
    return
  }

  // Handles cleanup for all close paths: button click, backdrop click, and Escape key
  const cleanupVideoModal = () => {
    const iframe = videoModal.querySelector('iframe')
    if (iframe) {
      // Tell YouTube to stop before clearing src — setting src='' alone doesn't
      // reliably stop audio in all browsers
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"stopVideo","args":""}',
          '*'
        )
      }
      iframe.src = ''
    }
    const transcriptSection = document.querySelector('.video-modal-transcript')
    if (transcriptSection) transcriptSection.innerHTML = ''
    videoModal.classList.remove('transcript', 'video')
    document.body.classList.remove('modal-open')
  }

  videoModal.addEventListener('close', cleanupVideoModal)

  // Intercept Escape at the keydown level so cleanup runs before the dialog closes
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && videoModal.open) {
      e.preventDefault()
      cleanupVideoModal()
      videoModal.close()
    }
  })

  const closeModal = () => videoModal.close()
  const closeBtn = document.querySelector('#video-modal-close')
  if (closeBtn) closeBtn.addEventListener('click', closeModal)

  // The dialog fills the viewport: its own empty area acts as the backdrop.
  // Require a primary pointer press and release there, preserving transcript
  // selection and keeping the dialog off Chrome's clickable-ancestor path.
  let backdropPointerId = null
  videoModal.addEventListener('pointerdown', event => {
    backdropPointerId =
      event.isPrimary && event.button === 0 && event.target === videoModal
        ? event.pointerId
        : null
  })
  videoModal.addEventListener('pointerup', event => {
    const shouldClose =
      event.isPrimary &&
      event.button === 0 &&
      event.pointerId === backdropPointerId &&
      event.target === videoModal
    backdropPointerId = null
    if (shouldClose) closeModal()
  })
  videoModal.addEventListener('pointercancel', () => {
    backdropPointerId = null
  })

  document.querySelectorAll('[data-video-id]').forEach(trigger => {
    trigger.addEventListener('click', e => {
      e.preventDefault()
      const videoId = trigger.dataset.videoId
      const iframe = videoModal.querySelector('iframe')
      if (iframe) {
        iframe.setAttribute(
          'src',
          `https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0&autoplay=1&enablejsapi=1`
        )
      }

      setTimeout(() => {
        videoModal.classList.remove('transcript')
        videoModal.classList.add('video')
        videoModal.showModal()
        document.body.classList.add('modal-open')
      }, 300)
    })
  })

  document.querySelectorAll('[data-video-transcript]').forEach(trigger => {
    trigger.addEventListener('click', e => {
      e.preventDefault()
      const transcriptSection = document.querySelector(
        '.video-modal-transcript'
      )
      if (transcriptSection) {
        transcriptSection.innerHTML = trigger.dataset.videoTranscript
      }
      videoModal.classList.remove('video')
      videoModal.classList.add('transcript')
      videoModal.showModal()
      document.body.classList.add('modal-open')
    })
  })
})

document.addEventListener('DOMContentLoaded', () => {
  const is_gform_loaded = typeof gform !== 'undefined' && gform !== null

  const REQUIRED_MESSAGE = 'This field is required.'

  // ZIP has no field type of its own — it is the ".5" sub-input of an Address
  // field, which Gravity Forms wraps in .ginput_address_zip. Match that wrapper
  // rather than input ids: forms here use several Address fields apiece to lay
  // out the parts separately (the signup form's ZIP is its own Address field),
  // so an id list silently misses whichever ones nobody remembered to add.
  const ZIP_INPUT_SELECTOR = '.ginput_address_zip input'

  // Inputs that carry a format rule on top of — or instead of — being required.
  const FORMAT_INPUT_SELECTOR =
    'input[type="email"], input[type="tel"], ' + ZIP_INPUT_SELECTOR

  // Wording mirrors what the server returns after a submit — Gravity Forms' own
  // messages for email/phone, and the theme's GF_GW_Req_Char_Length rules
  // (gravity-forms-functions.php) for ZIP — so the inline message and the
  // server-rendered one never disagree.
  const formatMessage = $input => {
    const value = jQuery.trim($input.val() || '')
    if (!value) return null

    const type = ($input.attr('type') || '').toLowerCase()

    if (type === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)
        ? null
        : 'Please enter a valid email address.'
    }

    if (type === 'tel') {
      // Only the "standard" (US) phone format has a regex behind it — Gravity
      // Forms leaves the international format unvalidated, so match that.
      if (!$input.closest('.gfield--phone-format-standard').length) return null
      return /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/.test(value)
        ? null
        : 'Please enter a valid phone number.'
    }

    if ($input.is(ZIP_INPUT_SELECTOR)) {
      if (value.length < 5) return 'Zip Code: must be at least 5 characters.'
      if (value.length > 5) return 'Zip Code: must be 5 or fewer characters.'
    }

    return null
  }

  // An empty required input is reported as such; its format is only judged once
  // there is something to judge.
  const inputMessage = $input => {
    if (
      $input.attr('aria-required') === 'true' &&
      !jQuery.trim($input.val() || '')
    ) {
      return REQUIRED_MESSAGE
    }
    return formatMessage($input)
  }

  // Blur validation writes its message after focus has already moved on, so a
  // screen reader never reads it — aria-describedby only speaks when the input
  // is focused again. Announce it through a per-form live region instead. The
  // region sits after the wrapper, not inside it, so an AJAX re-render (which
  // replaces the wrapper's markup) can't swap it out between updates.
  const announceRegion = formId => {
    const id = 'gform_announce_' + formId
    let region = document.getElementById(id)
    if (!region) {
      const wrapper = document.getElementById('gform_wrapper_' + formId)
      if (!wrapper) return null
      region = document.createElement('div')
      region.id = id
      region.className = 'sr-only'
      region.setAttribute('role', 'status')
      region.setAttribute('aria-live', 'polite')
      region.setAttribute('aria-atomic', 'true')
      wrapper.after(region)
    }
    return region
  }

  // Same shape as announceStatus: blank first so a repeated message is still a
  // real mutation, then write after a delay so it queues behind the newly
  // focused field's own announcement rather than being dropped by it.
  const validationAnnounceTimers = {}
  const announceValidation = (formId, text) => {
    const region = announceRegion(formId)
    if (!region) return
    clearTimeout(validationAnnounceTimers[formId])
    region.textContent = ''
    validationAnnounceTimers[formId] = setTimeout(
      () => {
        region.textContent = text
      },
      isIOSDevice ? 500 : 150
    )
  }

  // "Email: Please enter a valid email address" — the field is named because
  // focus is already on the next one when this is heard. Joined as one
  // comma-separated clause: iOS VoiceOver has cut period-joined live-region
  // text off after the first sentence.
  const validationAnnouncement = ($fieldContainer, messages) => {
    const $label = $fieldContainer.find('.gfield_label').first().clone()
    $label.find('.gfield_required').remove()
    const label = jQuery.trim($label.text())
    return messages
      .map(message => {
        const clause = message.replace(/\.$/, '')
        return label && clause.indexOf(label + ':') !== 0
          ? label + ': ' + clause
          : clause
      })
      .join(', ')
  }

  if (is_gform_loaded) {
    jQuery(document).on('gform_post_render', function (event, form_id) {
      // The visible "(Required)" text is redundant for assistive tech — the
      // input's own aria-required already announces it — so silence it there
      // while leaving it visible on screen.
      jQuery('#gform_' + form_id)
        .find('.gfield_required')
        .attr('aria-hidden', 'true')

      // Create the live region up front — a region inserted in the same tick
      // as its first update isn't reliably announced.
      announceRegion(form_id)

      // Point the submit button at the asset-size note (e.g. "PDF, 2.4 MB")
      // that renders after it, so screen readers announce it with the
      // button — but only when there's actually a note to point at.
      var $submitButton = jQuery('#gform_submit_button_' + form_id)
      var $assetSize = jQuery('#asset-size')
      if ($assetSize.length && jQuery.trim($assetSize.text())) {
        $submitButton.attr('aria-describedby', 'asset-size')
      } else {
        $submitButton.removeAttr('aria-describedby')
      }

      // Target genuinely-required inputs — Gravity Forms marks each one with
      // aria-required="true", which also correctly ignores optional sub-inputs
      // (e.g. Address Line 2) inside a grouped/required field — plus every
      // format-constrained input, which may be optional yet still needs its
      // format checked once something has been typed into it.
      jQuery('#gform_' + form_id)
        .find(
          '.gfield_contains_required input[aria-required="true"], .gfield_contains_required select[aria-required="true"], .gfield_contains_required textarea[aria-required="true"], ' +
            FORMAT_INPUT_SELECTOR
        )
        .on('blur', function () {
          var $fieldContainer = jQuery(this).closest('.gfield')
          // Evaluate the whole field. Address fields group several inputs under
          // a single .gfield, so validate every input we own there, not just
          // the one that was blurred.
          var $validatedInputs = $fieldContainer
            .find('[aria-required="true"]')
            .add($fieldContainer.find(FORMAT_INPUT_SELECTOR))
          var fieldId = $fieldContainer.attr('id')
          // Match Gravity Forms' own id scheme for its server-rendered
          // validation messages (validation_message_{form_id}_{field_id}),
          // so a message created here is indistinguishable from a real one.
          var fieldIdMatch = fieldId
            ? fieldId.match(/^field_(\d+)_(\d+)$/)
            : null
          var messageId = fieldIdMatch
            ? 'validation_message_' + fieldIdMatch[1] + '_' + fieldIdMatch[2]
            : null

          // Collect every rule currently failing in the field, deduped. A
          // grouped field (Address) can be failing the required rule on one
          // sub-input and the ZIP format rule on another at the same time, and
          // reporting only one of them reads as the other never firing.
          var messages = []
          $validatedInputs.each(function () {
            var message = inputMessage(jQuery(this))
            if (message && messages.indexOf(message) === -1)
              messages.push(message)
          })
          // Gravity Forms leads with the required error and appends the format
          // ones after a <br>, so compose them in the same order.
          var requiredIndex = messages.indexOf(REQUIRED_MESSAGE)
          if (requiredIndex > 0) {
            messages.splice(requiredIndex, 1)
            messages.unshift(REQUIRED_MESSAGE)
          }

          if (messages.length) {
            // Apply Gravity Forms error styling dynamically
            $fieldContainer.addClass('gfield_error')
            var $message = $fieldContainer.find(
              '.gfield_description.validation_message'
            )
            // Only announce when what the field reports actually changed —
            // re-blurring a field whose error the user has already heard (or
            // that was server-rendered with the same text) stays quiet.
            // Compared without whitespace since <br> has no text of its own.
            var previousText = $message.length
              ? $message.text().replace(/\s+/g, '')
              : ''
            if (messages.join('').replace(/\s+/g, '') !== previousText) {
              announceValidation(
                form_id,
                validationAnnouncement($fieldContainer, messages)
              )
            }
            if ($message.length === 0) {
              // Match Gravity Forms' own markup: same classes, and placed
              // directly above the input container (after the label/legend),
              // not appended to the end of the field.
              $message = jQuery(
                '<div class="gfield_description validation_message gfield_validation_message"></div>'
              )
              var $inputContainer = $fieldContainer
                .find('.ginput_container, .ginput_complex')
                .first()
              if ($inputContainer.length) {
                $inputContainer.before($message)
              } else {
                $fieldContainer.append($message)
              }
            }
            // Keep the text in step with what is currently wrong, so a
            // server-rendered format error survives a blur that doesn't fix it
            // and is replaced — not merely removed — when a different rule
            // starts failing. Every message here is a literal defined above,
            // so joining them as markup introduces nothing untrusted.
            $message.html(messages.join('<br>'))
            if (messageId) $message.attr('id', messageId)
          } else {
            // Remove the error once every input in the field passes
            $fieldContainer.removeClass('gfield_error')
            $fieldContainer.find('.validation_message').remove()

            // Also clear this field's entry from the form's validation summary
            if (fieldId) {
              var $summary = jQuery('#gform_wrapper_' + form_id).find(
                '.gform_validation_errors'
              )
              $summary
                .find('a[href="#' + fieldId + '"]')
                .closest('li')
                .remove()
              // Drop the whole summary once no errors remain
              if ($summary.find('li').length === 0) {
                $summary.remove()
              }
            }
          }

          // Sync aria-invalid/aria-describedby per input, since grouped fields
          // (e.g. Address) can have a mix of passing and failing sub-inputs.
          $validatedInputs.each(function () {
            var $input = jQuery(this)
            var isInvalid = inputMessage($input) !== null
            var describedBy = ($input.attr('aria-describedby') || '')
              .split(/\s+/)
              .filter(Boolean)
            // Drop our own message id plus anything else that no longer points
            // at a real element — e.g. Gravity Forms' own server-rendered
            // "validation_message_X_Y" id (a different scheme than ours) left
            // behind after a full-page submit, whose element we just removed.
            var stillValid = describedBy.filter(function (id) {
              return id !== messageId && document.getElementById(id)
            })

            if (isInvalid && messageId) {
              $input.attr('aria-invalid', 'true')
              $input.attr(
                'aria-describedby',
                stillValid.concat(messageId).join(' ')
              )
            } else {
              $input.removeAttr('aria-invalid')
              if (stillValid.length) {
                $input.attr('aria-describedby', stillValid.join(' '))
              } else {
                $input.removeAttr('aria-describedby')
              }
            }
          })
        })
    })
  }
})

document.addEventListener('DOMContentLoaded', () => {
  const cookieBanner = document.querySelector('.cky-consent-container')
  if (!cookieBanner) return
  const cookieBar = cookieBanner.querySelector('.cky-consent-bar')
  const cookieDescription = cookieBanner.querySelector('.cky-notice-des')
  const cookieLinks = cookieBanner.querySelectorAll('.cky-notice-des a')
  const cookieButton = cookieBanner.querySelector('button.cky-btn-accept')

  cookieBanner.classList.remove('cky-classic-top')
  cookieBanner.classList.add('cky-classic-bottom')
  cookieBar.style.backgroundColor = 'var(--color-usps-brand-blue)'
  cookieDescription.style.color = 'var(--color-white)'
  cookieLinks.forEach(link => {
    link.style.color = 'var(--color-white)'
  })
  cookieButton.removeAttribute('aria-label')
})

document.addEventListener('DOMContentLoaded', () => {
  var root = document.documentElement
  var ticking = false

  var updateScrollPercentage = () => {
    ticking = false
    var scrollable = document.body.scrollHeight - window.innerHeight
    var percentage = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0
    root.style.setProperty('--page-scroll-percentage', percentage + '%')
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(updateScrollPercentage)
      }
    },
    { passive: true }
  )
})

document.addEventListener('DOMContentLoaded', () => {
  const cookieAcceptButton = document.querySelector(
    '[data-cky-tag="accept-button"]'
  )

  if (cookieAcceptButton) {
    cookieAcceptButton.addEventListener('click', () => {
      document.querySelector('#status-msg').textContent = 'Cookies accepted.'

      setTimeout(() => {
        document.querySelector('#status-msg').textContent = ''
        document.querySelector('#content').focus()
      }, 2000)
    })
  }
})

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('p').forEach(p => {
    if (!p.children.length && p.textContent.trim() === '') {
      p.classList.add('is-empty')
    }
  })

  const globalFooterWrap = document.querySelector('.global-footer--wrap')
  if (globalFooterWrap) {
    document.documentElement.style.setProperty(
      '--footer-height',
      globalFooterWrap.getBoundingClientRect().height.toFixed(0) + 'px'
    )
  }

  window.addEventListener('resize', () => {
    if (globalFooterWrap) {
      document.documentElement.style.setProperty(
        '--footer-height',
        globalFooterWrap.getBoundingClientRect().height.toFixed(0) + 'px'
      )
    }
  })
})
